// Сгенерировано: python scripts/gen_zod.py
// ⚠ РУКАМИ НЕ ПРАВИТЬ. Источник — модели `server/fortify/api/schemas.py`;
// правка здесь потеряется при следующей генерации, а до тех пор схема клиента
// будет расходиться с ответом сервера молча. Форму ответа меняют на сервере,
// потом гоняют генератор; `tests/test_api_schemas.py` держит равенство файла
// на диске и свежей генерации.
import { z } from "zod";

/** Состояние поверхности входа. `kind` — код случая, приходит только в состоянии «П» (`first_run`); слово к нему — словарь (таск 72). ⚠⚠ **До входа поверхность не показывает ничего из данных компании** — ни чисел, ни названий поверхностей, ни учётных записей (`docs/SCREEN-MAP.md`, раздел `S-00`, «Состав»). F-000, шаг 6/10: демонстрационный режим и его подпись «ДЕМО» до входа убраны — состояние и только состояние. */
export const LoginSurfaceState = z.strictObject({
  state: z.enum(["П", "З"]),
  kind: z.literal("first_run").nullable().optional(),
});
export type LoginSurfaceState = z.infer<typeof LoginSurfaceState>;

/** Вход состоялся. Токена в теле нет — он уходит cookie `HttpOnly`. */
export const LoggedIn = z.strictObject({
  login: z.string(),
  goes_to: z.string(),
  assistant: z.boolean(),
  expires_at: z.iso.datetime({ offset: true }),
});
export type LoggedIn = z.infer<typeof LoggedIn>;

/** Строка списка своих сессий (`S-00`). Ни токена, ни его хеша здесь нет. */
export const SessionRow = z.strictObject({
  id: z.number().int(),
  opened_from: z.string().nullable().optional(),
  created_at: z.iso.datetime({ offset: true }),
  expires_at: z.iso.datetime({ offset: true }),
  current: z.boolean(),
});
export type SessionRow = z.infer<typeof SessionRow>;

/** Кто я и что мне открыто. */
export const Me = z.strictObject({
  login: z.string(),
  assistant: z.boolean(),
  may_grant_access: z.boolean(),
  must_change_password: z.boolean(),
  surfaces: z.record(z.string(), z.enum(["РЕД", "ТЧ", "НВ"])),
  sessions: z.array(SessionRow),
});
export type Me = z.infer<typeof Me>;

/** Сделано. Поверхность входа после этого показывает обычное состояние. */
export const Done = z.strictObject({
  state: z.literal("З"),
});
export type Done = z.infer<typeof Done>;

/** Непокрытая зависимость пресета: что открыто, чего не хватает, почему. `kind` — один из пяти видов: залежність, недосяжна, адреса, вкладка, сторона. */
export const UncoveredRow = z.strictObject({
  surface: z.string(),
  missing: z.string(),
  reason: z.string(),
  kind: z.string(),
});
export type UncoveredRow = z.infer<typeof UncoveredRow>;

/** Строка матрицы отметок: поверхность и то, что о ней знает сервер. */
export const MatrixRow = z.strictObject({
  code: z.string(),
  name: z.string(),
  section: z.string().nullable().optional(),
  merged_into: z.string().nullable().optional(),
  read_only_forever: z.boolean(),
  never_hidden: z.boolean(),
  linked: z.array(z.string()),
});
export type MatrixRow = z.infer<typeof MatrixRow>;

/** Пресет (`ENT-25`): отметки — по всем строкам матрицы. */
export const PresetRow = z.strictObject({
  id: z.number().int(),
  revision: z.string(),
  name: z.string().nullable().optional(),
  marks: z.record(z.string(), z.enum(["РЕД", "ТЧ", "НВ"])),
  effective: z.record(z.string(), z.enum(["РЕД", "ТЧ", "НВ"])),
  assistant: z.boolean(),
  full_access: z.boolean(),
  archived: z.boolean(),
  people: z.number().int(),
  uncovered: z.array(UncoveredRow),
});
export type PresetRow = z.infer<typeof PresetRow>;

export const AccessPresets = z.strictObject({
  presets: z.array(PresetRow),
  matrix: z.array(MatrixRow),
});
export type AccessPresets = z.infer<typeof AccessPresets>;

export const PreviewChange = z.strictObject({
  surface: z.string(),
  before: z.enum(["РЕД", "ТЧ", "НВ"]),
  after: z.enum(["РЕД", "ТЧ", "НВ"]),
});
export type PreviewChange = z.infer<typeof PreviewChange>;

export const PreviewPerson = z.strictObject({
  login: z.string(),
  changes: z.array(PreviewChange),
});
export type PreviewPerson = z.infer<typeof PreviewPerson>;

/** «Що зміниться» до сохранения: поимённо, по страницам. */
export const PresetPreview = z.strictObject({
  people: z.array(PreviewPerson),
  effective: z.record(z.string(), z.enum(["РЕД", "ТЧ", "НВ"])),
  uncovered: z.array(UncoveredRow),
  fingerprint: z.string(),
});
export type PresetPreview = z.infer<typeof PresetPreview>;

/** «Що отримає / втратить» человек при смене ролей — до записи, по страницам. */
export const RolesPreview = z.strictObject({
  changes: z.array(PreviewChange),
  fingerprint: z.string(),
});
export type RolesPreview = z.infer<typeof RolesPreview>;

export const PersonSession = z.strictObject({
  id: z.number().int(),
  opened_from: z.string().nullable().optional(),
  created_at: z.iso.datetime({ offset: true }),
  expires_at: z.iso.datetime({ offset: true }),
});
export type PersonSession = z.infer<typeof PersonSession>;

/** Учётная запись (`ENT-26`). Хеша пароля и пароля здесь нет никогда. */
export const PersonRow = z.strictObject({
  login: z.string(),
  status: z.string(),
  blocked: z.boolean(),
  may_grant_access: z.boolean(),
  effective: z.record(z.string(), z.enum(["РЕД", "ТЧ", "НВ"])),
  must_change_password: z.boolean(),
  last_login_at: z.iso.datetime({ offset: true }).nullable().optional(),
  presets: z.array(z.number().int()),
  sessions: z.array(PersonSession),
});
export type PersonRow = z.infer<typeof PersonRow>;

export const AccessPeople = z.strictObject({
  people: z.array(PersonRow),
});
export type AccessPeople = z.infer<typeof AccessPeople>;

/** Вход заведён или дан новый временный пароль: учётная запись и пароль, который система сгенерировала сама. ⚠ Пароль есть только в этом одном ответе (показ один раз, `Cache-Control: no-store`): ни в журнале, ни в логах, ни в других ответах его нет. */
export const PersonMade = z.strictObject({
  person: PersonRow,
  temp_password: z.string(),
});
export type PersonMade = z.infer<typeof PersonMade>;

/** Пресет в таком виде не сохраняется: код случая и поверхность. */
export const PresetRefusedAnswer = z.strictObject({
  kind: z.string(),
  subject: z.string().nullable().optional(),
});
export type PresetRefusedAnswer = z.infer<typeof PresetRefusedAnswer>;

/** Прогон синхронизации журналом (`facts.runs`). «Скільки документів прийшло» — `rows` (решение оркестратора 164, п. 2): строки ответа DnTrade по видам документов за прогон. ⚠ Строки, а не различные документы: накладная, прочитанная окном и по знаку, — две. Прогон, записанный до счёта, упавший или не читавший документов, числа не несёт — `null`, а не соседнее число (`docs/DATA.md` §8). */
export const SyncRunRow = z.strictObject({
  result: z.enum(["ok", "failed", "running"]),
  started_at: z.iso.datetime({ offset: true }),
  finished_at: z.iso.datetime({ offset: true }).nullable().optional(),
  seconds: z.number().nullable().optional(),
  changed: z.number().int(),
  appeared: z.number().int(),
  disappeared: z.number().int(),
  requests: z.number().int(),
  slowed_by_limit: z.boolean(),
  note_uk: z.string(),
  rows: z.number().int().nullable(),
  going: z.boolean(),
  retry_in_seconds: z.number().nullable(),
});
export type SyncRunRow = z.infer<typeof SyncRunRow>;

/** Состояние синхронизации (`facts.sync_state`), а не текст на экране. */
export const SyncStateRow = z.strictObject({
  kind: z.string(),
  note_uk: z.string().nullable().optional(),
  since: z.iso.datetime({ offset: true }).nullable().optional(),
  hours_ago: z.number().nullable().optional(),
  running_since: z.iso.datetime({ offset: true }).nullable().optional(),
});
export type SyncStateRow = z.infer<typeof SyncStateRow>;

/** Строка блока «Що не синхронізувалось»: вид документа и причина. ⚠ Вид — код источника (`payment-40`, `invoice-3`, `order`, справочник `partners`), а не название контрагента: персональных данных в строке нет. */
export const UnsyncedRow = z.strictObject({
  kind: z.string(),
  reason: z.enum(["truncated", "status0", "limit_exhausted", "http", "timeout", "connection", "request", "failed", "not_counted", "unfinished", "no_store"]).nullable(),
  reason_uk: z.string(),
});
export type UnsyncedRow = z.infer<typeof UnsyncedRow>;

/** Строка «Розкладів робіт»: одна работа планировщика (`ops.schedules`). ⚠ `next_run_at` — по фактическому расписанию планировщика (`Scheduler.next_run`), а не по значению настройки; `last_run_at` — когда работа шла на самом деле: обещанный час за выполненный не выдаётся. */
export const ScheduleRow = z.strictObject({
  job: z.string(),
  setting: z.string(),
  value: z.union([z.number().int(), z.string()]).nullable(),
  kind: z.enum(["scheduled", "off", "not_wired", "scheduler_stopped", "scheduler_never"]),
  last_run_at: z.iso.datetime({ offset: true }).nullable(),
  last_result: z.enum(["ok", "failed"]).nullable(),
  next_run_at: z.iso.datetime({ offset: true }).nullable(),
  reason_uk: z.string().nullable(),
});
export type ScheduleRow = z.infer<typeof ScheduleRow>;

/** Блок «Розклади робіт» (`docs/SCREEN-MAP.md`, `S-10`, п. (3)). */
export const ScheduleBlock = z.strictObject({
  scheduler: z.enum(["working", "stopped", "never"]),
  last_beat_at: z.iso.datetime({ offset: true }).nullable(),
  jobs: z.array(ScheduleRow),
});
export type ScheduleBlock = z.infer<typeof ScheduleBlock>;

/** Копия вне сервера: забрана ли на ПК владельца (F-090, 3б). Слова — компонент `offsite` здоровья. */
export const OffsiteBlock = z.strictObject({
  state: z.enum(["ok", "warn", "down", "unknown"]),
  note_uk: z.string(),
  configured: z.boolean(),
});
export type OffsiteBlock = z.infer<typeof OffsiteBlock>;

/** Блок «Резервна копія» (`docs/SCREEN-MAP.md`, `S-10`, п. (1)). `state` и `note_uk` — строка здоровья копии (`ops.health`), те же слова. */
export const BackupBlock = z.strictObject({
  offsite: OffsiteBlock,
  state: z.enum(["ok", "warn", "down", "unknown"]),
  note_uk: z.string(),
  taken: z.boolean(),
  first_at: z.iso.datetime({ offset: true }).nullable(),
  restore_checks: z.enum(["on", "off"]),
});
export type BackupBlock = z.infer<typeof BackupBlock>;

/** Строка блока «Система» `S-10`: применённая миграция (`ops.migrations`). Состояния «миграция ждёт» нет: миграции применяются при запуске образа (`docs/SCREEN-MAP.md`, `S-10`, п. (2)). */
export const MigrationRow = z.strictObject({
  revision: z.string(),
  applied_at: z.iso.datetime({ offset: true }).nullable(),
  reversible: z.boolean(),
});
export type MigrationRow = z.infer<typeof MigrationRow>;

/** Состояние данных (`S-10`): свежесть, прогоны, интервал. ⚠ Денежных чисел здесь нет ни одного — их нет и на поверхности. */
export const DataState = z.strictObject({
  mark: z.enum(["РЕД", "ТЧ", "НВ"]),
  indicator: z.enum(["fresh", "stale", "sync_down", "no_sync_yet"]),
  sync: SyncStateRow,
  runs: z.array(SyncRunRow),
  interval_minutes: z.number().int(),
  interval_min_minutes: z.number().int().nullable().optional(),
  unsynced: z.array(UnsyncedRow).nullable(),
  schedules: ScheduleBlock,
  backup: BackupBlock,
  migrations: z.array(MigrationRow),
});
export type DataState = z.infer<typeof DataState>;

/** Свежесть в шапке оболочки — у каждой действующей учётной записи. ⚠⚠ **Независимо от права на `S-10`** (решение оркестратора 84, `carry.md`, строка таска 27): свежесть — признак данных, а не поверхность, и снять её вместе с правом нельзя (`docs/SCREEN-MAP.md`, `S-10`, «Переход на поверхность»). Поэтому это отдельная ручка без стража поверхности, и в ней ровно то, что говорит шапка: случай индикатора и состояние синхронизации. ⚠ **Журнала прогонов и отметки пресета здесь нет по устройству:** шапке они не нужны, а журнал до экрана не доходит вовсе. Слова — словарь по коду случая `indicator`. */
export const HeaderFreshness = z.strictObject({
  indicator: z.enum(["fresh", "stale", "sync_down", "no_sync_yet"]),
  sync: SyncStateRow,
});
export type HeaderFreshness = z.infer<typeof HeaderFreshness>;

/** Одно дело «Потребує уваги» (F-013, решение 066). `key` — вид дела (слово — словарь), `screen` — экран, где оно решается и где пункту меню ставится число. `sync_down`, `backup_failed` — сбои: `count` 1, суммы нет. `state` `unknown` — источник не ответил: числа нет, `reason` — код причины. `amount` — сумма там, где она есть у источника. */
export const AttentionItem = z.strictObject({
  key: z.enum(["overdue", "rule_overdue", "limit", "gaps", "month_waiting", "wallets", "mismatch", "loan_terms", "payroll_rates", "unsynced", "sync_down", "backup_failed"]),
  screen: z.string(),
  count: z.number().int().nullable(),
  amount: z.string().nullable(),
  unsure: z.number().int().nullable(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
  tone: z.enum(["alarm", "neutral"]),
  state: z.enum(["known", "unknown"]),
  reason: z.enum(["attention_source_down", "attention_source_failed"]).nullable(),
  new: z.boolean(),
});
export type AttentionItem = z.infer<typeof AttentionItem>;

/** Ответ `GET /api/shell/attention`: дела по срочности, нулевых нет. */
export const Attention = z.strictObject({
  items: z.array(AttentionItem),
});
export type Attention = z.infer<typeof Attention>;

/** Ответ `PUT /api/shell/attention/seen`: ключи, записанные виденными. */
export const AttentionSeen = z.strictObject({
  keys: z.array(z.string()),
});
export type AttentionSeen = z.infer<typeof AttentionSeen>;

/** Резервная копия строки состояния: `ok` — последняя снялась в `at`; `failed` — последняя попытка не снялась (`at` — последняя удачная, если была); `none` — копий ещё не было; `unknown` — журнал копий не прочитан. */
export const BackupLastRow = z.strictObject({
  state: z.enum(["ok", "failed", "none", "unknown"]),
  at: z.iso.datetime({ offset: true }).nullable(),
});
export type BackupLastRow = z.infer<typeof BackupLastRow>;

/** Ответ `GET /api/shell/status`. `backup` `null` — `S-10` человеку закрыта. */
export const ShellStatus = z.strictObject({
  backup: BackupLastRow.nullable(),
});
export type ShellStatus = z.infer<typeof ShellStatus>;

/** Отказ со словами — у ручек вне таска 72 (обмен с воркером, общий отказ разбора тела `api.app`): их слова в словарь ещё не перенесены. */
export const Refused = z.strictObject({
  text_uk: z.string(),
});
export type Refused = z.infer<typeof Refused>;

/** Отказ входа или смены пароля: код случая, слово к нему — словарь. `retry_after_seconds` и `locked_until` — задержка после порога неудачных попыток (`CFG-138`) и момент, до которого пароль не проверяется; приходят только у `throttled`. «Заблоковано» приходит только после верного пароля. */
export const LoginRefused = z.strictObject({
  kind: z.enum(["wrong_credentials", "throttled", "account_blocked", "password_not_changed"]),
  retry_after_seconds: z.number().int().nullable().optional(),
  locked_until: z.iso.datetime({ offset: true }).nullable().optional(),
});
export type LoginRefused = z.infer<typeof LoginRefused>;

/** Вход кончился: код случая `session_expired`, слово — словарь. `return_to` — адрес, с которого человека завернули. */
export const SessionExpired = z.strictObject({
  kind: z.literal("session_expired"),
  return_to: z.string().nullable().optional(),
});
export type SessionExpired = z.infer<typeof SessionExpired>;

/** Отказ кодом случая без слов (решение оркестратора 106: слова к коду — в словаре). У «Завершити свою сесію»: `current_session` — текущую сессию завершает выход; `not_in_your_sessions` — сессии нет среди своих, и ответ не говорит, есть ли она у кого-то ещё. */
export const CaseRefused = z.strictObject({
  kind: z.string(),
});
export type CaseRefused = z.infer<typeof CaseRefused>;

/** Тело запроса не принято: код случая, имя поля и предел длины. Слово случая и слово поля — словарь (поле — ключом `field_<имя>`). */
export const FieldRefused = z.strictObject({
  kind: z.enum(["body_not_object", "field_missing", "field_not_text", "field_too_long"]),
  field: z.enum(["login", "password", "old", "new", "opened_from"]).nullable().optional(),
  limit: z.number().int().nullable().optional(),
});
export type FieldRefused = z.infer<typeof FieldRefused>;

/** Новый пароль не принят правилом длины. Пароля в ответе нет. */
export const WeakPasswordRefused = z.strictObject({
  kind: z.literal("password_too_short"),
  min_length: z.number().int(),
});
export type WeakPasswordRefused = z.infer<typeof WeakPasswordRefused>;

/** Хранилище приложению не подключено: старт в него не заходил. Код случая `storage_not_wired`, слово — словарь. ⚠ Для человека это отказ системы, а не неверные данные: поверхность входа показывает его тем же смыслом, что и молчание сервера (раздел `S-00`, `ПМ` «вход не проверен по вине системы»), иначе человек начнёт подбирать пароль. */
export const StorageNotWired = z.strictObject({
  kind: z.literal("storage_not_wired"),
});
export type StorageNotWired = z.infer<typeof StorageNotWired>;

/** Действие механизма прав не разрешено (`access.AccessRefused`): код случая и названный предмет — логин или пресет, если он есть. */
export const ActionRefused = z.strictObject({
  kind: z.string(),
  subject: z.string().nullable().optional(),
});
export type ActionRefused = z.infer<typeof ActionRefused>;

/** Словарь слов экрана (`api.screen_words()`): код случая → слово. Слово `null` — его ещё не написали (таск 74). До входа — только слова входа. */
export const ScreenWords = z.strictObject({
  words: z.record(z.string(), z.string().nullable()),
});
export type ScreenWords = z.infer<typeof ScreenWords>;

/** База числа (`Result.as_row()["base"]`): период, отбор, подпись. */
export const FreeBase = z.strictObject({
  period: z.array(z.string()).nullable(),
  filter: z.string().nullable(),
  note: z.string().nullable(),
});
export type FreeBase = z.infer<typeof FreeBase>;

/** Пробел «Число неповне»: код случая ядра, числа к слову и направление. ⚠ Слов ядра нет (таск 75, `G117`): слово — словарь по `kind`. Числа — целые (`core.cases.Case`: `count`, `total`); другое пришло бы от нового места ядра и обязано покраснеть здесь, а не доехать до слова непрочитанным. */
export const FreeGap = z.strictObject({
  kind: z.string().min(1),
  numbers: z.record(z.string(), z.union([z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$"))])),
  direction: z.enum(["завищено", "занижено", "невідомо"]),
  state: z.literal("in_development").nullable(),
});
export type FreeGap = z.infer<typeof FreeGap>;

/** Число величины так, как его показывает поверхность (`free_money.number_fields`). ⚠ Одно место на двух потребителей: ручку `S-01` и блок дашборда `S-52` (раздел `S-52`: число блока — то же, что на поверхности-источнике). */
export const FreeNumber = z.strictObject({
  code: z.string(),
  on_date: z.iso.date(),
  before_launch: z.boolean(),
  value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  state: z.string(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
  side: z.enum(["завищено", "занижено", "невідомо", "в обидва боки"]).nullable(),
  gaps: z.array(FreeGap),
  base: FreeBase,
  stub: z.boolean(),
  unverified: z.boolean(),
  simulated: z.boolean(),
  as_of: z.string().nullable(),
  as_of_hours_ago: z.number().nullable(),
  horizon_days: z.number().int().nullable(),
});
export type FreeNumber = z.infer<typeof FreeNumber>;

/** Часть строки разложения: код части, код реестра, число, пояснение кодом. */
export const FreePart = z.strictObject({
  kind: z.string().min(1),
  code: z.string(),
  value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  note_kind: z.string().min(1).nullable(),
  note_uk: z.string().nullable(),
  state: z.string().min(1).nullable(),
  reason: z.string().min(1).nullable(),
});
export type FreePart = z.infer<typeof FreePart>;

/** Строка разложения «Вільно»: код строки по месту слагаемого `FRM-01`. */
export const FreeLine = z.strictObject({
  kind: z.string().min(1),
  sign: z.enum(["+", "−"]),
  source: z.string(),
  value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  state: z.string().min(1).nullable(),
  reason: z.string().min(1).nullable(),
  parts: z.array(FreePart),
});
export type FreeLine = z.infer<typeof FreeLine>;

/** Шестая строка «В розробці» (`DEC-O451`): числа нет, направление есть. */
export const FreePending = z.strictObject({
  kind: z.string().min(1),
  value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  state: z.string().min(1),
  reason: z.string().min(1),
  direction: z.enum(["завищено", "занижено", "невідомо"]),
});
export type FreePending = z.infer<typeof FreePending>;

/** Счётчик строки поставщиков: штуки, сумма (`None` — неизвестна), направление (`None` — не ошибка, а решение владельца: «Не потребує оплати», 064). */
export const FreeTally = z.strictObject({
  count: z.number().int(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
});
export type FreeTally = z.infer<typeof FreeTally>;

/** Накладная в части строки: поставщик, накладная, термін → день оплаты, залишок. */
export const FreeDutyRow = z.strictObject({
  part: z.enum(["overdue", "in_horizon"]),
  partner: z.string(),
  partner_title: z.string().nullable(),
  document: z.string(),
  number: z.string().nullable(),
  invoice_day: z.iso.date().nullable(),
  due: z.iso.date(),
  pay_day: z.iso.date(),
  offset: z.number().int(),
  left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
});
export type FreeDutyRow = z.infer<typeof FreeDutyRow>;

/** Строка поставщиков «Вільно» (F-004): две части, база, счётчики и строки. Числа — ядра (`FRM-24`); ручка их не считает (`api/free_duties.py`). */
export const FreeDuties = z.strictObject({
  overdue: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  in_horizon: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  on: z.iso.date(),
  edge: z.iso.date(),
  launch: z.iso.date().nullable(),
  no_terms: FreeTally.nullable(),
  no_payment: FreeTally.nullable(),
  unknown: FreeTally.nullable(),
  unconfirmed: FreeTally.nullable(),
  unplaced: FreeTally.nullable(),
  rows: z.array(FreeDutyRow),
});
export type FreeDuties = z.infer<typeof FreeDuties>;

/** Вход формулы в объяснении `core.explain` — ссылки реестра. */
export const ExplainInput = z.strictObject({
  name: z.string(),
  source_kind: z.string(),
  id: z.string().nullable(),
  setting_id: z.string().nullable(),
  entity_id: z.string().nullable(),
});
export type ExplainInput = z.infer<typeof ExplainInput>;

/** Объяснение формулы `core.explain`. Экран его словами не печатает. */
export const Explanation = z.strictObject({
  code: z.string(),
  definition: z.string().nullable(),
  definition_term: z.string().nullable(),
  definition_note: z.string().nullable(),
  name_uk: z.string(),
  expression: z.string().nullable(),
  scale: z.string().nullable(),
  inputs: z.array(ExplainInput),
  decision_refs: z.array(z.string()),
  states: z.record(z.string(), z.string()),
  settings: z.array(z.string()),
  recompute: z.string(),
  stub: z.boolean(),
  unverified: z.boolean(),
  rule: z.boolean(),
});
export type Explanation = z.infer<typeof Explanation>;

/** «Вільно» на день (`GET /api/s-01/free`): число, пять строк, шестая, объяснение. */
export const Free = z.strictObject({
  code: z.string(),
  on_date: z.iso.date(),
  before_launch: z.boolean(),
  value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  state: z.string(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
  side: z.enum(["завищено", "занижено", "невідомо", "в обидва боки"]).nullable(),
  gaps: z.array(FreeGap),
  base: FreeBase,
  stub: z.boolean(),
  unverified: z.boolean(),
  simulated: z.boolean(),
  as_of: z.string().nullable(),
  as_of_hours_ago: z.number().nullable(),
  horizon_days: z.number().int().nullable(),
  mark: z.enum(["РЕД", "ТЧ"]),
  input_at: z.string().nullable(),
  lines: z.array(FreeLine),
  buffer_days: z.number().int().nullable(),
  pending_line: FreePending.nullable(),
  in_development_count: z.number().int(),
  duties: FreeDuties.nullable(),
  explain: Explanation,
});
export type Free = z.infer<typeof Free>;

/** Вход «Як пораховано»: код ядра, значение либо числа к слову. Значение — строка (сумма, день) либо целое (счёт): генератор переводит это объединением (таск 28 v2, решение оркестратора 114 (г)). */
export const FreeFact = z.strictObject({
  kind: z.string().min(1),
  value: z.union([z.string(), z.number().int()]).nullable(),
  numbers: z.record(z.string(), z.number().int()),
});
export type FreeFact = z.infer<typeof FreeFact>;

/** Раскрытая формула: число, период, код базы и входы кодами ядра. */
export const FreeOpenedResult = z.strictObject({
  code: z.string(),
  value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  state: z.string(),
  period: z.array(z.string()).nullable(),
  basis: z.string().nullable(),
  facts: z.array(FreeFact),
});
export type FreeOpenedResult = z.infer<typeof FreeOpenedResult>;

/** Раскрытие строки до формулы (`GET /api/s-01/free/open/{code}`). */
export const FreeOpened = z.strictObject({
  code: z.string(),
  on_date: z.iso.date(),
  result: FreeOpenedResult,
});
export type FreeOpened = z.infer<typeof FreeOpened>;

/** Документ раскрытия (`core.documents.DocRef.as_row`): вклад со знаком. */
export const FreeDocument = z.strictObject({
  kind: z.string().min(1),
  external_id: z.string(),
  day: z.iso.date().nullable(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  status: z.string().nullable(),
  wallet: z.string().nullable(),
  article: z.string().nullable(),
});
export type FreeDocument = z.infer<typeof FreeDocument>;

/** Раскрытие входа до документов (`GET /api/s-01/free/documents/{input}`). Документов нет — `documents` пусто, `leads_to` и код случая `kind` называют, куда ведёт раскрытие; пустой список был бы другим ответом — «документов ноль». */
export const FreeDocuments = z.strictObject({
  input: z.string(),
  on_date: z.iso.date(),
  documents: z.array(FreeDocument).nullable(),
  leads_to: z.string().nullable(),
  kind: z.string().min(1).nullable(),
});
export type FreeDocuments = z.infer<typeof FreeDocuments>;

/** Размер блока в ячейках сетки (`data/dashboard-blocks.json`, поле `size`). */
export const BlockSize = z.strictObject({
  cols: z.number().int(),
  rows: z.number().int(),
});
export type BlockSize = z.infer<typeof BlockSize>;

/** Блок раскладки: источник, код величины, случай блока и число источника. */
export const DashboardBlock = z.strictObject({
  code: z.string(),
  source: z.string(),
  value_code: z.string().nullable(),
  type: z.string(),
  main: z.boolean(),
  size: BlockSize.nullable(),
  place: z.number().int(),
  input_at: z.string().nullable(),
  kind: z.enum(["number", "in_development", "cannot_show", "no_set_day", "digest"]),
  number: FreeNumber.nullable(),
  in_development_count: z.number().int().nullable(),
  pending_way: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
});
export type DashboardBlock = z.infer<typeof DashboardBlock>;

/** «Мій дашборд» (`GET /api/s-52/dashboard`). */
export const Dashboard = z.strictObject({
  kind: z.enum(["blocks", "no_source_granted"]),
  assembled_by: z.enum(["system", "person"]),
  blocks: z.array(DashboardBlock),
});
export type Dashboard = z.infer<typeof Dashboard>;

/** Лимит, действующий на паре или на поставщике целиком (`params.suppliers.limits`). `tracked` — отслеживается ли приближение и превышение: только у «задано» (решение 068); «не питали» и 0 сигнала не дают. */
export const SupplierLimit = z.strictObject({
  state: z.enum(["задано", "без ліміту", "не питали"]),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  source: z.enum(["група", "постачальник"]).nullable(),
  tracked: z.boolean(),
});
export type SupplierLimit = z.infer<typeof SupplierLimit>;

/** «Хто вніс» и «Хто підтвердив» записи (`G141`, `П-3`, таск 13e). `entered_by` — учётная запись, с которой сделана запись (ставит система). `confirmed_by` — логин учётной записи `ENT-26`; у записи до `G141` со свободным текстом — `null`, а текст — в `confirmed_by_legacy` (прежний, за учётную запись не выдаётся). `day` — дата основания. */
export const SupplierBasis = z.strictObject({
  entered_by: z.string().nullable(),
  confirmed_by: z.string().nullable(),
  confirmed_by_legacy: z.string().nullable(),
  day: z.iso.date().nullable(),
  note: z.string().nullable(),
  note_masked_before_g145: z.boolean(),
});
export type SupplierBasis = z.infer<typeof SupplierBasis>;

/** Редакция условий пары: «Діє з» и «Діє по» (днём раньше следующей). Такой её отдаёт дерево `S-23` — без основания записи: дерево показывает условия, а кто и на каком основании их внёс, видно на карточке `S-12` (`SupplierTerms`). */
export const GoodsTerms = z.strictObject({
  revision_id: z.string(),
  valid_from: z.iso.date(),
  valid_to: z.iso.date().nullable(),
  payment: z.string(),
  days: z.number().int().nullable(),
  event: z.string(),
  event_readable: z.boolean(),
  limit: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  importance: z.string().nullable(),
});
export type GoodsTerms = z.infer<typeof GoodsTerms>;

/** Редакция условий группы на карточке `S-12` — с основанием записи. `basis` — «Хто вніс / Хто підтвердив» и «Підстава» той версии записи `ENT-03`, которой редакция создана (`review-13e.md` Н-8); `null` — у записи основания нет. ⚠ Поле обязательно (решение оркестратора 180, таск 13f): карточка отдаёт его у каждой редакции; дерево `S-23` его не отдаёт вовсе и потому описано своей моделью (`GoodsTerms`). */
export const SupplierTerms = z.strictObject({
  revision_id: z.string(),
  valid_from: z.iso.date(),
  valid_to: z.iso.date().nullable(),
  payment: z.string(),
  days: z.number().int().nullable(),
  event: z.string(),
  event_readable: z.boolean(),
  limit: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  importance: z.string().nullable(),
  basis: SupplierBasis.nullable(),
});
export type SupplierTerms = z.infer<typeof SupplierTerms>;

/** Группа товара поставщика — пара «постачальник + категорія» по ID. Группы поставщика — пары с записью условий и категории из его приходных накладных (таск 14). `no_group` — именованная группа «без групи» для номенклатуры без категории (`S-23`, границы): её слово — словарь экрана, `category` — идентификатор, а не название. `terms` — своя редакция на день; `inherited_from` — своей нет, действуют условия предка (эта категория); `needs_setup` — `ПН`: по группе есть приходная накладная, а условий нет ни своих, ни наследованных — даты платежа нет, в горизонт H обязательство не входит (`FRM-24.warning`). */
export const SupplierGroup = z.strictObject({
  category: z.string(),
  title: z.string().nullable(),
  in_directory: z.boolean(),
  no_group: z.boolean(),
  terms: SupplierTerms.nullable(),
  inherited_from: z.string().nullable(),
  inherited_terms: GoodsTerms.nullable(),
  needs_setup: z.boolean(),
  setup_reason: z.enum(["no_terms", "event_unreadable"]).nullable(),
  limit: SupplierLimit,
  revisions: z.array(SupplierTerms),
  default_from: z.iso.date(),
});
export type SupplierGroup = z.infer<typeof SupplierGroup>;

/** «Умови задані у N груп з M» (`SCREEN-MAP.md:1353`): `with_terms` — группы со своими или наследованными условиями на день, `total` — все группы карточки. Считает сервер (решение оркестратора 172). */
export const SupplierGroupCounts = z.strictObject({
  with_terms: z.number().int(),
  total: z.number().int(),
});
export type SupplierGroupCounts = z.infer<typeof SupplierGroupCounts>;

/** Перечни полей `ENT-03` — из реестра сущностей, не копией в коде (решение оркестратора 172, `review-13.md` Н-6): умова оплати, подія відліку, стратегічна важливість и их умолчания. `no_group` — идентификатор группы «без групи» для записи условий на эту пару. */
export const TermsValues = z.strictObject({
  payment: z.array(z.string()),
  event: z.array(z.string()),
  event_default: z.string().nullable(),
  importance: z.array(z.string()),
  importance_default: z.string().nullable(),
  no_group: z.string(),
  penalty_kind: z.array(z.string()),
  penalty_unit: z.array(z.string()),
});
export type TermsValues = z.infer<typeof TermsValues>;

/** Платёж графика: `due` — срок после переноса выходного (025), `paid` — `null`, если выполнение неизвестно («невідомо»). */
export const OldDebtPayment = z.strictObject({
  due: z.iso.date().nullable(),
  amount: z.string(),
  paid: z.string().nullable(),
  state: z.enum(["paid", "late", "ahead", "unknown"]),
});
export type OldDebtPayment = z.infer<typeof OldDebtPayment>;

/** Действующая договорённость, из которой выведен график (`ENT-36`). */
export const OldDebtSchedule = z.strictObject({
  key: z.string(),
  day: z.iso.date(),
  gist: z.string(),
  amount: z.string(),
  first_day: z.iso.date(),
});
export type OldDebtSchedule = z.infer<typeof OldDebtSchedule>;

/** Старый долг поставщика на день `on` карточки (F-003, решения 020, 063). Все суммы — строками с копейками, «ми винні» плюсом (052). Число — одно (правило 4): `left` = `PartnerSettlement.old_debt_left`, тот же, что у колонки `S-45`. `null` — «невідомо», а не ноль (правило 10); причина — код `why_code` (слово — словарь клиента; текст с кодами — только журнал). Аванс «нам винні» — отдельным числом (`TRAP-07`), не нетто. `total` («Разом», решение 020) — `Settlement.owed` со знаком «ми винні» плюсом, справочно: с треком не складывается; база одна — `base`. `schedule` — `set`, если график выведен из договорённости, иначе `none` («графіка немає — внесіть домовленість», правило 13); `not_yet` — договорённость записана позже дня `on` («графіка немає на цей день»). `overdue` — «прострочено за графіком» с базой `overdue_base`; `null` при `schedule=none` и «невідомо». `payments` — прошедшие и будущие платежи в пределах `CFG-05`; `count` — сколько будущих всего, `last` — последний. `unknown` — сколько оплат ушло «невідомо» с направлением `unknown_direction`. `note` — «обіцяно X, боргу Y», если сумма покрывает остаток. */
export const SupplierOldDebt = z.strictObject({
  state: z.enum(["owed", "none", "paid_off", "unknown", "no_track"]),
  base: z.string(),
  opening: z.string().nullable(),
  corrections: z.string().nullable(),
  paid: z.string().nullable(),
  left: z.string().nullable(),
  advance: z.string(),
  total: z.string().nullable(),
  schedule: z.enum(["set", "none", "not_yet"]),
  agreement: OldDebtSchedule.nullable(),
  payments: z.array(OldDebtPayment),
  overdue: z.string().nullable(),
  overdue_base: z.string(),
  nearest: OldDebtPayment.nullable(),
  last: OldDebtPayment.nullable(),
  count: z.number().int().nullable(),
  paid_off_on: z.iso.date().nullable(),
  why_code: z.enum(["no_launch", "opening_draft", "opening_missing", "opening_before_launch", "opening_after_launch", "settle_refused", "no_track", "before_launch", "old_debt_unknown"]).nullable(),
  unknown: z.string(),
  unknown_direction: z.string(),
  note: z.string().nullable(),
});
export type SupplierOldDebt = z.infer<typeof SupplierOldDebt>;

/** Счётчик с суммой: штуки и сумма (`None` — хоть одна неизвестна). */
export const SupplierCount = z.strictObject({
  count: z.number().int(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type SupplierCount = z.infer<typeof SupplierCount>;

/** «невідомо ≤ X» — остаток не посчитан (отказ разнесения, тело не внесено); `direction` — «завищено»: X — граница сверху. */
export const SupplierUnknown = z.strictObject({
  count: z.number().int(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
});
export type SupplierUnknown = z.infer<typeof SupplierUnknown>;

/** Лимит против «До виплати» (`supplier_tracks.limit_signal`, 068): `share` — доля, % к сотой; `left` — «залишилось», `over` — «перевищено на»; `used` — известная часть «До виплати». `direction` — «завищено», когда «До виплати» может быть завышено (сальдо не подтверждено). */
export const SupplierLimitSignal = z.strictObject({
  state: z.enum(["not_tracked", "no_limit", "within", "near", "over", "unknown"]),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  used: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  share: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  over: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  source: z.enum(["група", "постачальник"]).nullable(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
  threshold: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  may_exceed: z.boolean(),
});
export type SupplierLimitSignal = z.infer<typeof SupplierLimitSignal>;

/** Лимит группы со своим ответом — против «До виплати» группы и её подгрупп без своего ответа (068, 3). */
export const SupplierGroupLimitSignal = z.strictObject({
  state: z.enum(["not_tracked", "no_limit", "within", "near", "over", "unknown"]),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  used: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  share: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  over: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  source: z.enum(["група", "постачальник"]).nullable(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
  threshold: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  may_exceed: z.boolean(),
  category: z.string(),
});
export type SupplierGroupLimitSignal = z.infer<typeof SupplierGroupLimitSignal>;

/** Трек «Поточна робота» (F-006) — ответ `supplier_tracks.tracks` как есть. `to_pay` («До виплати», `FRM-61`) = прострочено + сьогодні + найближчі + пізніше + без умов (`today` — день оплаты = день, `soon` — следующие H дней, 069); «не потребує оплати» (064) и «невідомо» — вне суммы, счётчиками. Старый долг с треком не складывается (020), аванс — отдельно (`TRAP-07`). `launched=false` — до запуска: «до запуску зобов'язань немає» (021). База — `basis`, у «прострочено» — `overdue_basis` (правило 5). Горизонт «не знаю» (`CFG-04` не целое) — `soon` и `later` пусты (не 0,00), их Σ — `ahead`; горизонт задан — `ahead` пусто. */
export const SupplierCurrent = z.strictObject({
  launched: z.boolean(),
  basis: z.string(),
  overdue_basis: z.string(),
  overdue: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  today: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  soon: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  later: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  ahead: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  to_pay: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  no_terms: SupplierCount,
  no_payment: SupplierCount,
  unknown: SupplierUnknown,
  advance: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  unconfirmed: z.boolean(),
  limit: SupplierLimitSignal,
  group_limits: z.array(SupplierGroupLimitSignal),
});
export type SupplierCurrent = z.infer<typeof SupplierCurrent>;

/** Перенос накладной `ENT-203` (F-008) — отметка «перенесено». `term` — термін (день по условиям, 025), `day` — день оплаты (последний живой перенос; `missed` — его день прошёл без оплаты, платёж вернулся к терміну: «планували … — не сплачено», тогда `planned` — тот день). `was` — «Було» последнего переноса, `kind`/`why` — вид и текст причины, `who`/`when` — кто и когда, `count` — живых переносов, `undo` — ключ записи последнего (отмена — только его). */
export const InvoiceMoved = z.strictObject({
  term: z.iso.date(),
  day: z.iso.date(),
  planned: z.iso.date(),
  was: z.iso.date(),
  kind: z.string().nullable(),
  why: z.string().nullable(),
  who: z.string(),
  when: z.iso.date(),
  count: z.number().int(),
  missed: z.boolean(),
  undo: z.string(),
});
export type InvoiceMoved = z.infer<typeof InvoiceMoved>;

/** Накладная раскрытия `РД`: номер и день из фактов, группа, термін → день оплаты (025), тіло, оплачено (тіло − залишок), залишок (`TRAP-27`). Остаток неизвестен — `paid`/`left` пусты, `body` — граница «≤». `moved` — перенос `ENT-203` (F-008); не переносили — пусто. */
export const SupplierInvoice = z.strictObject({
  key: z.string(),
  document: z.string(),
  number: z.string().nullable(),
  invoice_day: z.iso.date().nullable(),
  category: z.string().nullable(),
  category_title: z.string().nullable(),
  due: z.iso.date().nullable(),
  pay_day: z.iso.date().nullable(),
  body: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  paid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  part: z.enum(["overdue", "today", "soon", "later", "ahead", "no_terms", "no_payment", "unknown"]),
  moved: InvoiceMoved.nullable().optional(),
});
export type SupplierInvoice = z.infer<typeof SupplierInvoice>;

/** Группа раскрытия `РД`: накладные с одним днём оплаты (`day`, `key` = день) или — без дня — одной частью (`no_terms` · `no_payment` · `unknown`). `amount` — Σ остатков группы (`None` — хоть один неизвестен: «невідомо ≤ X»), `invoices` — ключи `SupplierInvoice.key`. Σ групп с днём и «без умов» = «До виплати» (считает сервер, `supplier_tracks.groups`). */
export const SupplierInvoiceGroup = z.strictObject({
  key: z.string(),
  part: z.enum(["overdue", "today", "soon", "later", "ahead", "no_terms", "no_payment", "unknown"]),
  day: z.iso.date().nullable(),
  offset: z.number().int().nullable(),
  count: z.number().int(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  invoices: z.array(z.string()),
});
export type SupplierInvoiceGroup = z.infer<typeof SupplierInvoiceGroup>;

/** Смуга «До виплати»: проценты шкалы к сотой (`supplier_tracks.strip`) — доли частей, заливка, черта порога и черта лимита. Экран только рисует: суммы на клиенте не делятся. `null` у `threshold_at`/`limit_at` — лимита нет. Горизонт «не знаю» — `soon`/`later` пусты, доля после сегодня — `ahead`. */
export const SupplierStrip = z.strictObject({
  overdue: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  today: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  soon: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  later: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  ahead: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  no_terms: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  fill: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  threshold_at: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  limit_at: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type SupplierStrip = z.infer<typeof SupplierStrip>;

/** Ячейка шкалы «Поточна робота»: дни оплаты `start`…`end` (025; пусто — открытая граница: «90+ днів тому», «далі»), часть, Σ остатков, число и ключи накладных (`SupplierInvoice.key`). Пустая — с нулём. `rank` — место по сумме среди непустых (1 — наибольшая), `label` — подписывать сумму, `height` — доля потолка 0…1, `broken` — сумма выше потолка (столбик обрезан). */
export const SupplierSlot = z.strictObject({
  key: z.string(),
  part: z.enum(["overdue", "today", "soon", "later", "ahead", "no_terms", "no_payment", "unknown"]),
  start: z.iso.date().nullable(),
  end: z.iso.date().nullable(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  count: z.number().int(),
  invoices: z.array(z.string()),
  rank: z.number().int().nullable(),
  label: z.boolean(),
  height: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  broken: z.boolean(),
});
export type SupplierSlot = z.infer<typeof SupplierSlot>;

/** Шкала «До виплати» по дню оплаты (F-006, «одна спільна шкала»): 90+ · 61–90 · 31–60 днів тому, дни −30…−1, «сьогодні», H дней по дню, шесть недель, «далі». Σ ячеек + `no_terms` = «До виплати»; «без умов» дня оплаты не имеют — не на оси (039). `cap` — потолок высоты (95-й перцентиль непустых), непустых нет — пусто. */
export const SupplierTimeline = z.strictObject({
  basis: z.string(),
  cap: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  no_terms: SupplierCount,
  slots: z.array(SupplierSlot),
});
export type SupplierTimeline = z.infer<typeof SupplierTimeline>;

export const SupplierNextPayment = z.strictObject({
  day: z.iso.date(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
});
export type SupplierNextPayment = z.infer<typeof SupplierNextPayment>;

/** Факты под суммой: «сьогодні», «прострочено» и «найдовше N дн.» (0 — просрочки нет), «наступний» — ближайший день оплаты после дня и Σ его накладных (`None` — следующего платежа нет). */
export const SupplierFacts = z.strictObject({
  today: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  overdue: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  longest_days: z.number().int(),
  next: SupplierNextPayment.nullable(),
});
export type SupplierFacts = z.infer<typeof SupplierFacts>;

/** Старый долг в строке списка (F-003): состояние, остаток, «прострочено за графіком»; `null` — «невідомо» или графика нет, а не ноль. */
export const SupplierOldDebtBrief = z.strictObject({
  state: z.enum(["owed", "none", "paid_off", "unknown", "no_track"]),
  left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  overdue: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  schedule: z.enum(["set", "none", "not_yet"]),
  why_code: z.enum(["no_launch", "opening_draft", "opening_missing", "opening_before_launch", "opening_after_launch", "settle_refused", "no_track", "before_launch", "old_debt_unknown"]).nullable(),
  paid_off_on: z.iso.date().nullable(),
});
export type SupplierOldDebtBrief = z.infer<typeof SupplierOldDebtBrief>;

/** Запись ленты договорённостей `ENT-36` (только чтение в таске 12). */
export const SupplierAgreement = z.strictObject({
  day: z.iso.date(),
  gist: z.string(),
  with_whom: z.string().nullable(),
  active: z.boolean(),
  changes_terms: z.boolean(),
  basis: SupplierBasis.nullable(),
});
export type SupplierAgreement = z.infer<typeof SupplierAgreement>;

/** Отметки карточки `ENT-02` на день: «критичний», стоп отгрузок, «не потребує оплати», штраф. Карточки нет — все `false`, штрафа нет. Штраф структурно (`G140`, таск 13e): `penalty` — примітка (у записи до `G140` — прежний текст целиком); `penalty_kind`, `penalty_size` (строкой), `penalty_unit`, `penalty_grace_days`; `has_penalty` — «є штраф»: вид и размер с единицей. `basis` — «Хто вніс / Хто підтвердив» записи карточки. Поля 13e обязательны (решение оркестратора 180, таск 13f). */
export const SupplierFlags = z.strictObject({
  critical: z.boolean(),
  shipping_stop: z.boolean(),
  no_payment_needed: z.boolean(),
  penalty: z.string().nullable(),
  penalty_kind: z.string().nullable(),
  penalty_size: z.string().nullable(),
  penalty_unit: z.string().nullable(),
  penalty_grace_days: z.number().int().nullable(),
  has_penalty: z.boolean(),
  basis: SupplierBasis.nullable(),
});
export type SupplierFlags = z.infer<typeof SupplierFlags>;

/** Карточка поставщика (`GET /api/s-12/suppliers/{partner}`). `can_edit` — есть ли у человека правка всей поверхности; при `ТЧ` действий ввода нет. `gone` — контрагент исчез из справочника DnTrade (`ЗН`). */
export const SupplierCard = z.strictObject({
  mark: z.enum(["РЕД", "ТЧ", "НВ"]),
  can_edit: z.boolean(),
  on: z.iso.date(),
  partner: z.string(),
  title: z.string().nullable(),
  gone: z.boolean(),
  side: z.string().nullable(),
  card: SupplierFlags,
  limit: SupplierLimit,
  groups: z.array(SupplierGroup),
  counts: SupplierGroupCounts,
  values: TermsValues,
  accounts: z.array(z.string()),
  agreements: z.array(SupplierAgreement),
  old_debt: SupplierOldDebt,
  current: SupplierCurrent,
  invoices: z.array(SupplierInvoice),
  invoice_groups: z.array(SupplierInvoiceGroup),
  strip: SupplierStrip.nullable(),
  timeline: SupplierTimeline,
  facts: SupplierFacts,
});
export type SupplierCard = z.infer<typeof SupplierCard>;

/** `needs_setup` — `ПН`: сторона не проставлена, а приходная накладная есть. */
export const SupplierRow = z.strictObject({
  partner: z.string(),
  title: z.string().nullable(),
  standing: z.enum(["not_supplier", "gone"]).nullable(),
  critical: z.boolean(),
  shipping_stop: z.boolean(),
  has_invoice: z.boolean(),
  needs_setup: z.boolean(),
  groups_with_terms: z.number().int(),
  limit_state: z.enum(["задано", "без ліміту", "не питали"]),
  current: SupplierCurrent,
  old_debt: SupplierOldDebtBrief,
});
export type SupplierRow = z.infer<typeof SupplierRow>;

/** Счётчики наверху списка: «картку заведено у N з M», «умови задані у N з M», «ліміт вказано у N з M». `total` — M, строки списка (поставщики со стороной и контрагенты без стороны с приходной накладной, `ПН`); считает сервер (решение оркестратора 172). `side_unknown` — контрагенты справочника, чья сторона ещё не проставлена. */
export const SupplierCounts = z.strictObject({
  suppliers: z.number().int(),
  with_terms: z.number().int(),
  with_limit: z.number().int(),
  critical: z.number().int(),
  total: z.number().int(),
  side_unknown: z.number().int(),
});
export type SupplierCounts = z.infer<typeof SupplierCounts>;

/** Список поставщиков (`GET /api/s-12/suppliers`). */
export const Suppliers = z.strictObject({
  mark: z.enum(["РЕД", "ТЧ", "НВ"]),
  can_edit: z.boolean(),
  on: z.iso.date(),
  counts: SupplierCounts,
  rows: z.array(SupplierRow),
});
export type Suppliers = z.infer<typeof Suppliers>;

/** Затронутое обязательство: сумма, прежняя и новая дата, сдвиг в днях. Новой даты нет — `reason` называет почему (`no_invoice_day` и коды `params`). */
export const RevisionAffected = z.strictObject({
  key: z.string(),
  revision_id: z.string().nullable(),
  paid: z.enum(["open", "partly", "unknown"]),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  before: z.iso.date().nullable(),
  after: z.iso.date().nullable(),
  shift_days: z.number().int().nullable(),
  reason: z.string().nullable(),
  auto: z.boolean(),
  moved_to: z.iso.date().nullable().optional(),
});
export type RevisionAffected = z.infer<typeof RevisionAffected>;

/** `complete=false` — «неповне»: в сумме нет частично оплаченных и тех, чей статус оплаты неизвестен; они названы строками. */
export const RevisionTotal = z.strictObject({
  count: z.number().int(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  complete: z.boolean(),
});
export type RevisionTotal = z.infer<typeof RevisionTotal>;

/** Изменение «Вільно», если записать редакцию и перенести выбранные (F-005). `amount` — Δ «Вільно» = −Δ строки поставщиков `FRM-24` (`line_before` → `line_after`), на день `on` с горизонтом `horizon_days`; `basis` — база словами. `complete=false` — у части переносимых остаток неизвестен, `direction` — куда ошибается «Вільно» стане. `counted=false` — не посчитано, `reason` — код причины (`free_before_launch`, `free_no_horizon`). */
export const FreeDelta = z.strictObject({
  counted: z.boolean(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  reason: z.string().nullable(),
  line_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  line_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  complete: z.boolean().nullable(),
  direction: z.string().nullable(),
  basis: z.string().nullable(),
  on: z.iso.date(),
  horizon_days: z.number().int().nullable(),
});
export type FreeDelta = z.infer<typeof FreeDelta>;

/** Поле редакции, которое меняется: имя поля `ENT-03` и значения до и после. */
export const RevisionChange = z.strictObject({
  field: z.string(),
  before: z.string().nullable(),
  after: z.string().nullable(),
});
export type RevisionChange = z.infer<typeof RevisionChange>;

/** `ЗМ`: что изменит редакция и какие обязательства построены по прежней. Ничего не записано. */
export const RevisionPreview = z.strictObject({
  partner: z.string(),
  category: z.string(),
  effective_from: z.iso.date(),
  changes: z.array(RevisionChange),
  affected: z.array(RevisionAffected),
  total: RevisionTotal,
  free: FreeDelta,
});
export type RevisionPreview = z.infer<typeof RevisionPreview>;

/** Перенесённое обязательство: прежняя и новая дата платежа. */
export const MovedRow = z.strictObject({
  key: z.string(),
  before: z.iso.date().nullable(),
  after: z.iso.date(),
});
export type MovedRow = z.infer<typeof MovedRow>;

/** Перенесённые на редакцию обязательства и карточка после переноса. */
export const RevisionApplied = z.strictObject({
  moved: z.array(MovedRow),
  card: SupplierCard,
});
export type RevisionApplied = z.infer<typeof RevisionApplied>;

/** Отказ записи `S-12`: код случая и код причины, без слов и без эха присланного (решение оркестратора 106). */
export const SupplierRefused = z.strictObject({
  kind: z.string().min(1),
  reason: z.string().nullable().optional(),
});
export type SupplierRefused = z.infer<typeof SupplierRefused>;

/** Узел дерева категорий DnTrade на паре с поставщиком. `pair` — пара «постачальник + категорія» есть: по категории пришла его приходная накладная или записаны условия. `no_group` — группа «без групи» (`category` — идентификатор `NO_GROUP`). `gone` — категория исчезла из справочника (`ЗН`): условия сохраняются. `terms` — своя редакция на день; `inherited_from` — своей нет, действуют условия предка; `needs_setup` — `ПН`: накладная есть, условий нет — даты платежа нет, в горизонт H не входит (`FRM-24.warning`). */
export const GoodsNode = z.strictObject({
  category: z.string(),
  title: z.string().nullable(),
  parent: z.string().nullable(),
  level: z.number().int().nullable(),
  gone: z.boolean(),
  no_group: z.boolean(),
  pair: z.boolean(),
  terms: GoodsTerms.nullable(),
  inherited_from: z.string().nullable(),
  needs_setup: z.boolean(),
  in_directory: z.boolean(),
});
export type GoodsNode = z.infer<typeof GoodsNode>;

/** «Ч»: пар у поставщика, из них с условиями (своими или наследованными) и в `ПН`. Считает сервер. */
export const GoodsCounts = z.strictObject({
  pairs: z.number().int(),
  with_terms: z.number().int(),
  needs_setup: z.number().int(),
});
export type GoodsCounts = z.infer<typeof GoodsCounts>;

/** Дерево категорий по паре с поставщиком (`GET /api/s-23/suppliers/{partner}`). Порядок узлов — справочника, внутри уровня сначала `ПН`; подкатегория — под своим родителем; «без групи» — последней и всегда. `tree_loaded=false` — справочник категорий ещё не пришёл (`П`). `export_trap` — категория пуста у всех товаров: ловушка выгрузки, а не «груп немає». `duplicates` — идентификаторы категорий с одинаковым названием: называются, не сливаются. */
export const GoodsTree = z.strictObject({
  mark: z.enum(["РЕД", "ТЧ", "НВ"]),
  can_edit: z.boolean(),
  on: z.iso.date(),
  partner: z.string(),
  title: z.string().nullable(),
  gone: z.boolean(),
  tree_loaded: z.boolean(),
  export_trap: z.boolean(),
  nodes: z.array(GoodsNode),
  counts: GoodsCounts,
  duplicates: z.array(z.array(z.string())),
});
export type GoodsTree = z.infer<typeof GoodsTree>;

/** Поставщик в таблице: старый борг, поточні, аванс и «Разом» — из одного разнесения (`Settlement`); статус — последняя сверка файлом (`ENT-28`). `None` в сумме — «невідомо» (`ENT-21` не подтверждён), а не ноль. */
export const ReportSupplier = z.strictObject({
  partner: z.string(),
  title: z.string().nullable(),
  old_debt: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  current: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  advance: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  direction: z.enum(["we_owe", "they_owe", "none"]).nullable(),
  state: z.enum(["agrees", "gap", "unchecked"]),
  checked_on: z.iso.date().nullable(),
  gap: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  gap_side: z.enum(["ours_more", "theirs_more"]).nullable(),
  confirmed_by: z.string().nullable(),
  confirmed_on: z.iso.date().nullable(),
  opening_gap: z.boolean(),
});
export type ReportSupplier = z.infer<typeof ReportSupplier>;

/** Контрагент последнего файла без связки: название и «Залишок» файла. */
export const ReportUnmatched = z.strictObject({
  title: z.string(),
  closing: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  direction: z.enum(["we_owe", "they_owe", "none"]),
});
export type ReportUnmatched = z.infer<typeof ReportUnmatched>;

/** Поставщик справочника для связки названия из файла (не строка таблицы). */
export const ReportLinkOption = z.strictObject({
  partner: z.string(),
  title: z.string(),
});
export type ReportLinkOption = z.infer<typeof ReportLinkOption>;

/** Загруженный файл отчёта: `sha` — отпечаток байт; по нему «Записати» и «виправити за файлом» называют файл, который видел человек. */
export const ReportFileInfo = z.strictObject({
  sha: z.string(),
  uploaded_by: z.string(),
  uploaded_at: z.iso.datetime({ offset: true }),
  size: z.number().int(),
});
export type ReportFileInfo = z.infer<typeof ReportFileInfo>;

/** Кто и когда подтвердил (`ENT-28`, журнал записи); день — киевский. */
export const ReportHeld = z.strictObject({
  by: z.string(),
  on: z.iso.date().nullable(),
});
export type ReportHeld = z.infer<typeof ReportHeld>;

/** Блок «Звірка за <місяць>» (F-085): последний закрытый месяц. `closed` — конец прошлого месяца не раньше `CFG-37`. `waiting` — сколько поставщиков «все сходиться» записало бы, `unknown` — «Разом» невідоме (не пишется), `confirmed` — уже подтверждено. `blocked` — почему кнопки нет: `sync_failed` или `no_known` (ни у кого «Разом» не известен). */
export const ReportMonth = z.strictObject({
  day: z.iso.date(),
  closed: z.boolean(),
  waiting: z.number().int(),
  unknown: z.number().int(),
  unknown_names: z.array(z.string()),
  confirmed: z.number().int(),
  held: ReportHeld.nullable(),
  blocked: z.enum(["sync_failed", "no_known"]).nullable(),
  our_base: z.string(),
});
export type ReportMonth = z.infer<typeof ReportMonth>;

/** Таблица `S-45` (`GET /api/s-45/suppliers`). */
export const ReportSuppliers = z.strictObject({
  mark: z.enum(["РЕД", "ТЧ", "НВ"]),
  can_upload: z.boolean(),
  can_link: z.boolean(),
  can_confirm: z.boolean(),
  can_fix: z.boolean(),
  month: ReportMonth,
  on: z.iso.date(),
  file: ReportFileInfo.nullable(),
  rows: z.array(ReportSupplier),
  total_count: z.number().int(),
  agree_count: z.number().int(),
  gap_count: z.number().int(),
  unchecked_count: z.number().int(),
  gap_total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  checked_through: z.iso.date().nullable(),
  unmatched: z.array(ReportUnmatched),
  link_options: z.array(ReportLinkOption),
});
export type ReportSuppliers = z.infer<typeof ReportSuppliers>;

/** Строка файла, где сверка не сошлась: состояние, расхождение с направлением и причина словами (`SupplierLine.reason_uk`). */
export const ReportProblem = z.strictObject({
  partner: z.string(),
  title: z.string().nullable(),
  state: z.string(),
  gap: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  gap_side: z.enum(["ours_more", "theirs_more"]).nullable(),
  reason: z.string(),
  absent: z.array(z.string()),
});
export type ReportProblem = z.infer<typeof ReportProblem>;

/** Сколько строк файла и на какую сумму (модуль); `None` — суммы нет (валютные договоры не складываются с гривной). */
export const ReportCounted = z.strictObject({
  count: z.number().int(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  direction: z.enum(["we_owe", "they_owe", "none"]).nullable(),
});
export type ReportCounted = z.infer<typeof ReportCounted>;

/** Что записал «Записати»: записано, расхождения, состав файла. */
export const ReportLoaded = z.strictObject({
  reconciled: z.boolean(),
  complete: z.boolean(),
  openings: z.number().int(),
  corrections: z.number().int(),
  acts: z.number().int(),
  links: z.number().int(),
  problems: z.array(ReportProblem),
  unmatched: ReportCounted,
  not_suppliers: ReportCounted,
  foreign: ReportCounted,
});
export type ReportLoaded = z.infer<typeof ReportLoaded>;

/** `ENT-21` плана: «було» (`None` — сальдо нет) → «стане» из «На початок» файла. Сумма модулем, направление — словом (знак 052). */
export const ReportPlanOpening = z.strictObject({
  was: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  was_owes: z.enum(["we_owe", "they_owe", "none"]).nullable(),
  will: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  will_owes: z.enum(["we_owe", "they_owe", "none"]),
});
export type ReportPlanOpening = z.infer<typeof ReportPlanOpening>;

/** `ENT-39` плана: день, сумма модулем, «збільшує»/«зменшує» наш борг, документ DnTrade. */
export const ReportPlanFix = z.strictObject({
  day: z.iso.date(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  sign: z.enum(["increase", "decrease"]),
  document: z.string(),
});
export type ReportPlanFix = z.infer<typeof ReportPlanFix>;

/** `ENT-28` плана: «Разом» на конец периода и «їхня сума» — две величины рядом (`TRAP-07`), расхождение с направлением. */
export const ReportPlanAct = z.strictObject({
  ours: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  ours_owes: z.enum(["we_owe", "they_owe", "none"]),
  theirs: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  theirs_owes: z.enum(["we_owe", "they_owe", "none"]),
  gap: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  gap_side: z.enum(["ours_more", "theirs_more"]).nullable(),
});
export type ReportPlanAct = z.infer<typeof ReportPlanAct>;

/** Один поставщик плана: «було → стане» по `ENT-21`, `ENT-39`, `ENT-28`. */
export const ReportPlanRow = z.strictObject({
  partner: z.string(),
  title: z.string().nullable(),
  opening: ReportPlanOpening.nullable(),
  fixes: z.array(ReportPlanFix),
  act: ReportPlanAct.nullable(),
  quiet: z.boolean(),
});
export type ReportPlanRow = z.infer<typeof ReportPlanRow>;

/** Просмотр файла до записи (`POST /api/s-45/report`): в `ENT-*` записей 0; поля `ReportLoaded` — то, что «Записати» запишет. `fingerprint` — отпечаток плана: «Записати» передаёт его вместе с `sha`, сервер строит план заново. `same_file` — те же байты уже лежали; `can_apply` — «Записати» доступна (право, синхронизация не упала, план не пуст): единственный источник права — сервер. Пуст ли план — по счётчикам `openings`/`corrections`/`acts`/`links`. */
export const ReportPreview = z.strictObject({
  reconciled: z.boolean(),
  complete: z.boolean(),
  openings: z.number().int(),
  corrections: z.number().int(),
  acts: z.number().int(),
  links: z.number().int(),
  problems: z.array(ReportProblem),
  unmatched: ReportCounted,
  not_suppliers: ReportCounted,
  foreign: ReportCounted,
  sha: z.string(),
  fingerprint: z.string(),
  file: ReportFileInfo,
  same_file: z.boolean(),
  can_apply: z.boolean(),
  sync_failed: z.boolean(),
  rows: z.array(ReportPlanRow),
});
export type ReportPreview = z.infer<typeof ReportPreview>;

/** Ответ «Записати»: либо `loaded` (записано), либо `preview` — план разошёлся с просмотром (прошла синхронизация), записей 0, «дані змінилися». */
export const ReportApplied = z.strictObject({
  loaded: ReportLoaded.nullable(),
  preview: ReportPreview.nullable(),
});
export type ReportApplied = z.infer<typeof ReportApplied>;

/** Ответ «все сходиться»: сколько записано, «невідомо» ключами (не подтверждены), сколько уже было подтверждено и кем; обе базы подписаны. */
export const ReportMonthConfirmed = z.strictObject({
  day: z.iso.date(),
  written: z.number().int(),
  unknown: z.array(z.string()),
  unknown_names: z.array(z.string()),
  already: z.number().int(),
  held: ReportHeld.nullable(),
  our_base: z.string(),
  their_base: z.string(),
});
export type ReportMonthConfirmed = z.infer<typeof ReportMonthConfirmed>;

export const ReportRegisterRow = z.strictObject({
  day: z.iso.date(),
  kind: z.enum(["invoice", "payment", "return", "fix", "other"]),
  number: z.string().nullable(),
  income: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  outcome: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  running: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  running_owes: z.enum(["we_owe", "they_owe", "none"]).nullable(),
});
export type ReportRegisterRow = z.infer<typeof ReportRegisterRow>;

/** Детальные строки поставщика: реестр с `CFG-37` плюс `ENT-39`. Суммы остатка — модулем, направление — словом. */
export const ReportRegister = z.strictObject({
  partner: z.string(),
  opening: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  opening_owes: z.enum(["we_owe", "they_owe", "none"]).nullable(),
  closing: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  closing_owes: z.enum(["we_owe", "they_owe", "none"]).nullable(),
  rows: z.array(ReportRegisterRow),
});
export type ReportRegister = z.infer<typeof ReportRegister>;

/** Строка «найбільші борги»: строка таблицы `S-45` как есть, без пересчёта. */
export const DigestDebt = z.strictObject({
  partner: z.string(),
  title: z.string().nullable(),
  total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  direction: z.enum(["we_owe", "they_owe", "none"]),
  state: z.enum(["agrees", "gap", "unchecked"]),
});
export type DigestDebt = z.infer<typeof DigestDebt>;

/** Сводка `S-45`: счётчики сверки, месяц, время файла и верх таблицы по «Разом». */
export const DigestReconciliation = z.strictObject({
  on: z.iso.date(),
  checked_through: z.iso.date().nullable(),
  file_at: z.iso.datetime({ offset: true }).nullable(),
  total_count: z.number().int(),
  agree_count: z.number().int(),
  gap_count: z.number().int(),
  unchecked_count: z.number().int(),
  month: ReportMonth,
  top: z.array(DigestDebt),
});
export type DigestReconciliation = z.infer<typeof DigestReconciliation>;

/** Сводка `S-10`: индикатор, последняя синхронизация, интервал, резервная копия. */
export const DigestDataState = z.strictObject({
  indicator: z.enum(["fresh", "stale", "sync_down", "no_sync_yet"]),
  sync: SyncStateRow,
  interval_minutes: z.number().int(),
  backup: BackupBlock,
  mismatch: z.number().int().nullable(),
  mismatch_reason: z.string().min(1).nullable(),
  all_ok: z.boolean(),
});
export type DigestDataState = z.infer<typeof DigestDataState>;

/** Сводка `S-12`: счётчики списка поставщиков на день. */
export const DigestSuppliers = z.strictObject({
  on: z.iso.date(),
  counts: SupplierCounts,
});
export type DigestSuppliers = z.infer<typeof DigestSuppliers>;

/** Строка «старий борг за графіком» (020, 063): неоплаченная часть платежа графика в его день; в блоке «Прострочено» — «прострочено за графіком» (`day` пусто). Ни в одну сумму текущего трека не входит. */
export const CalendarOld = z.strictObject({
  partner: z.string(),
  title: z.string().nullable(),
  day: z.iso.date().nullable(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
});
export type CalendarOld = z.infer<typeof CalendarOld>;

/** Поставщик в дне, в «Прострочено» или в «Сьогодні»: Σ остатков его накладных (`supplier_tracks.days`), накладные `РД` и их группы по дню оплаты (`offset` — «прострочено N дн.»), «найдовше N дн.», флаг «критичний». */
export const CalendarSupplier = z.strictObject({
  partner: z.string(),
  title: z.string().nullable(),
  standing: z.enum(["not_supplier", "gone"]).nullable(),
  critical: z.boolean(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  count: z.number().int(),
  longest_days: z.number().int(),
  unconfirmed: z.boolean(),
  invoices: z.array(SupplierInvoice),
  groups: z.array(SupplierInvoiceGroup),
});
export type CalendarSupplier = z.infer<typeof CalendarSupplier>;

/** «Прострочено» (день оплаты < дня) или «Сьогодні» (день оплаты = дню). Поставщики — от большего остатка к меньшему; `partners`, `invoices`, `others` («і ще N»), `longest_days` («найдовше N дн.») — счёт сервера. */
export const CalendarBlock = z.strictObject({
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  suppliers: z.array(CalendarSupplier),
  old: z.array(CalendarOld),
  partners: z.number().int(),
  invoices: z.number().int(),
  others: z.number().int(),
  longest_days: z.number().int(),
});
export type CalendarBlock = z.infer<typeof CalendarBlock>;

/** День оплаты после дня календаря: Σ текущего трека по поставщику; `in_free` — не позже края `on + H` (уже вычтен у «Вільно»); пусто — горизонт «не знаю» (`CFG-04` не целое): вычтен ли день, неизвестно. `offset` — дней от дня календаря. Строки старого долга — рядом, не в сумме. */
export const CalendarDay = z.strictObject({
  day: z.iso.date(),
  offset: z.number().int(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  in_free: z.boolean().nullable(),
  suppliers: z.array(CalendarSupplier),
  old: z.array(CalendarOld),
  partners: z.number().int(),
  invoices: z.number().int(),
  others: z.number().int(),
});
export type CalendarDay = z.infer<typeof CalendarDay>;

/** «невідомо ≤ X — сум у днях може бути більше»: накладные с днём оплаты в раскрытии, не в суммах дней. */
export const CalendarUnknown = z.strictObject({
  count: z.number().int(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
  invoices: z.array(SupplierInvoice),
});
export type CalendarUnknown = z.infer<typeof CalendarUnknown>;

/** Старый долг есть или «невідомо», а строк графика нет: причина — кодом (`why_code` F-003) и состоянием графика. */
export const CalendarOldAbsent = z.strictObject({
  partner: z.string(),
  title: z.string().nullable(),
  state: z.enum(["owed", "none", "paid_off", "unknown", "no_track"]),
  schedule: z.enum(["set", "none", "not_yet"]),
  why_code: z.enum(["no_launch", "opening_draft", "opening_missing", "opening_before_launch", "opening_after_launch", "settle_refused", "no_track", "before_launch", "old_debt_unknown"]).nullable(),
});
export type CalendarOldAbsent = z.infer<typeof CalendarOldAbsent>;

/** Календарь оплат поставщикам по дню (`GET /api/s-30/calendar`, F-007). Одна `supplier_tracks.days` поверх `DayTracks` списка `S-12`: суммы дней, поставщика в дне и блоков считает сервер, клиент только рисует. Просрочка — одним блоком наверху, прошлые дни сумм не несут; `today` пусто — день календаря выходной («сьогодні вихідний — платежів немає», 025). `total` — прострочено + сьогодні + дни; глубже `end` (`CFG-05`) — `deeper`. Без дня оплаты — `no_terms` (039), «не потребує оплати» — `no_payment` (064). `launched=false` — до запуска (021); `launch` пусто — `CFG-37` не задан; `edge` пусто — горизонт «не знаю» (`CFG-04` не целое), причина — `edge_basis`. */
export const SupplierCalendar = z.strictObject({
  mark: z.enum(["РЕД", "ТЧ", "НВ"]),
  on: z.iso.date(),
  edge: z.iso.date().nullable(),
  end: z.iso.date(),
  launch: z.iso.date().nullable(),
  launched: z.boolean(),
  basis: z.string(),
  overdue_basis: z.string(),
  edge_basis: z.string(),
  overdue: CalendarBlock,
  today: CalendarBlock.nullable(),
  days: z.array(CalendarDay),
  total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  no_terms: SupplierCount,
  no_payment: SupplierCount,
  unknown: CalendarUnknown,
  unconfirmed: SupplierUnknown,
  deeper: SupplierCount,
  old_absent: z.array(CalendarOldAbsent),
});
export type SupplierCalendar = z.infer<typeof SupplierCalendar>;

/** Накладная предпросмотра переноса (F-008): «Було» (действующий день оплаты, считает сервер), «Стало», термін, часть до и после (`overdue` · `today` · `soon` · `later`) — или отказ кодом (`refused`), тогда «Стало» и часть после пусты. */
export const CalendarMovePlan = z.strictObject({
  key: z.string(),
  was: z.iso.date().nullable(),
  became: z.iso.date().nullable(),
  term: z.iso.date().nullable(),
  part_before: z.enum(["overdue", "today", "soon", "later", "ahead", "no_terms", "no_payment", "unknown"]).nullable(),
  part_after: z.enum(["overdue", "today", "soon", "later", "ahead", "no_terms", "no_payment", "unknown"]).nullable(),
  refused: z.string().nullable(),
});
export type CalendarMovePlan = z.infer<typeof CalendarMovePlan>;

/** Число предпросмотра «було → стане» с базой (правило 5). */
export const CalendarMoveSide = z.strictObject({
  before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
});
export type CalendarMoveSide = z.infer<typeof CalendarMoveSide>;

/** «Що зміниться», если перенести (`POST /api/s-30/moves/preview`): ничего не записано. Накладные по ключам (отказы — кодом у ключа), Δ «Вільно» — строка `FRM-24` на день сервера с переносом и без (`free`, та же `horizon.duties`, что у «Вільно»), «Прострочено» и «у горизонті» було → стане. Перенос меняет день, но не сумму: `total` до и после — одно. */
export const CalendarMovePreview = z.strictObject({
  on: z.iso.date(),
  day: z.iso.date(),
  plans: z.array(CalendarMovePlan),
  free: FreeDelta,
  overdue: CalendarMoveSide,
  in_horizon: CalendarMoveSide,
  total: CalendarMoveSide,
});
export type CalendarMovePreview = z.infer<typeof CalendarMovePreview>;

/** Записанный перенос: ключ обязательства, ключ записи `ENT-203`, «Було» (сервер) и «Стало». */
export const CalendarMoveRow = z.strictObject({
  key: z.string(),
  record: z.string(),
  was: z.iso.date(),
  became: z.iso.date(),
});
export type CalendarMoveRow = z.infer<typeof CalendarMoveRow>;

/** Записанный пакет переносов (`POST /api/s-30/moves`) — одной транзакцией. */
export const CalendarMoves = z.strictObject({
  moves: z.array(CalendarMoveRow),
});
export type CalendarMoves = z.infer<typeof CalendarMoves>;

/** Отменённые переносы (`POST /api/s-30/moves/undo`): записи в архиве, платёж вернулся на «Було». */
export const CalendarUndone = z.strictObject({
  undone: z.array(CalendarMoveRow),
});
export type CalendarUndone = z.infer<typeof CalendarUndone>;

/** Отказ переноса: код случая, код причины и ключ, на котором отказ. */
export const CalendarMoveRefused = z.strictObject({
  kind: z.string().min(1),
  reason: z.string().nullable().optional(),
  key: z.string().nullable().optional(),
});
export type CalendarMoveRefused = z.infer<typeof CalendarMoveRefused>;

/** Запись истории настройки: с какого дня, что, кто. Ничего не удаляется. */
export const SettingHistoryRow = z.strictObject({
  applies_from: z.iso.date(),
  value: z.union([z.boolean(), z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")), z.string().min(1)]).nullable(),
  actor: z.string(),
  at: z.iso.datetime({ offset: true }).nullable(),
});
export type SettingHistoryRow = z.infer<typeof SettingHistoryRow>;

/** Подушка: «ручне» или «розрахунок» и дни своей статистики (решение 049). `own_days` пусто — дата запуска `CFG-37` не задана («своєї статистики ще немає»); `formula` — число `FRM-03` на день (пусто — не порахувати, причина — `formula_missing`). */
export const SettingBufferNote = z.strictObject({
  mode: z.enum(["manual", "formula"]),
  own_days: z.number().int().nullable(),
  launch: z.iso.date().nullable(),
  formula: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  formula_basis: z.string().nullable(),
  formula_missing: z.array(z.string()),
});
export type SettingBufferNote = z.infer<typeof SettingBufferNote>;

/** Окно базы средних на сегодня (F-033) — тем же окном, что `FRM-38`. `own_months` — месяцев окна после запуска `CFG-37` (пусто — запуск не задан); `next_entry` — когда войдёт следующий закончившийся месяц; `first_own_entry` — первый свой; `all_own_from` — с какого дня «останні 3 місяці» все свои (дата совета). Окна нет — `missing` кодами причин (`gap_base_not_yet` и т. п.). */
export const SettingBaseNote = z.strictObject({
  day_from: z.iso.date().nullable(),
  day_to: z.iso.date().nullable(),
  months: z.number().int().nullable(),
  own_months: z.number().int().nullable(),
  launch: z.iso.date().nullable(),
  next_entry: z.iso.date().nullable(),
  first_own_entry: z.iso.date().nullable(),
  all_own_from: z.iso.date().nullable(),
  missing: z.array(z.string().min(1)),
});
export type SettingBaseNote = z.infer<typeof SettingBaseNote>;

/** Строка настройки `S-11`: значение, источник, «змінено <день> · <хто>», история, границы с основанием, последствия, право правки. Строка-ссылка (`list`, F-033) значения не показывает: сводка `count` («поправок: N» / «пар: N») и экран правки `edit_on`, `editable` — всегда нет. */
export const SettingRow = z.strictObject({
  code: z.string(),
  name: z.string(),
  kind: z.enum(["days", "money", "bool", "months", "percent", "choice", "list"]),
  value: z.union([z.boolean(), z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")), z.string().min(1)]).nullable(),
  source: z.enum(["storage", "registry"]),
  changed_on: z.iso.date().nullable(),
  changed_by: z.string().nullable(),
  history: z.array(SettingHistoryRow),
  min: z.union([z.boolean(), z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")), z.string().min(1)]).nullable(),
  max: z.union([z.boolean(), z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")), z.string().min(1)]).nullable(),
  options: z.union([z.array(z.number().int()), z.array(z.string().min(1))]).nullable(),
  limits_why: z.string(),
  consequence_more: z.string().nullable(),
  consequence_less: z.string().nullable(),
  on_change: z.string().nullable(),
  editable: z.boolean(),
  buffer: SettingBufferNote.nullable(),
  base: SettingBaseNote.nullable(),
  count: z.number().int().nullable(),
  edit_on: z.string().nullable(),
});
export type SettingRow = z.infer<typeof SettingRow>;

/** `GET /api/s-11/settings`: группа «Гроші і горизонт» на сегодня сервера. */
export const SettingsGroup = z.strictObject({
  mark: z.enum(["РЕД", "ТЧ", "НВ"]),
  on: z.iso.date(),
  group: z.string(),
  rows: z.array(SettingRow),
});
export type SettingsGroup = z.infer<typeof SettingsGroup>;

/** Вычитаемая строка «Вільно» было → стане; Δ пусто — числа нет. */
export const SettingLineDelta = z.strictObject({
  line: z.string(),
  name: z.string(),
  part: z.string(),
  before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  delta: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  touched: z.boolean(),
});
export type SettingLineDelta = z.infer<typeof SettingLineDelta>;

/** Часть строки поставщиков `FRM-24` было → стане (текстом ядра). */
export const SettingSide = z.strictObject({
  before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type SettingSide = z.infer<typeof SettingSide>;

/** `FRM-38` одной стороны превью: число, окно базы, месяцы; числа нет — причины кодом (`missing`), а не 0,00. */
export const SettingRegularSide = z.strictObject({
  value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  day_from: z.iso.date().nullable(),
  day_to: z.iso.date().nullable(),
  months: z.number().int().nullable(),
  missing: z.array(z.string().min(1)),
});
export type SettingRegularSide = z.infer<typeof SettingRegularSide>;

/** Регулярні витрати за місяць було → стане — с окнами обеих баз (правило 5). */
export const SettingRegular = z.strictObject({
  before: SettingRegularSide,
  after: SettingRegularSide,
});
export type SettingRegular = z.infer<typeof SettingRegular>;

/** `POST /api/s-11/preview`: «Вільно» було → стане по строкам, ничего не пишет. `main_number`: `counted` — посчитано ядром; `unchanged` — настройка главного числа не двигает. Δ «Вільно» = −Σ Δ строк (`delta`); `complete=false` — затронутые строки без числа (`unknown`), сторона ошибки — `direction`. Граница «Вільно» календаря `S-30` — `edge_before` → `edge_after`. */
export const SettingPreview = z.strictObject({
  code: z.string(),
  value_before: z.union([z.boolean(), z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")), z.string().min(1)]).nullable(),
  value_after: z.union([z.boolean(), z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")), z.string().min(1)]),
  on: z.iso.date(),
  main_number: z.enum(["counted", "unchanged"]),
  horizon_before: z.number().int().nullable(),
  horizon_after: z.number().int().nullable(),
  free_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  delta: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  complete: z.boolean(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
  unknown: z.array(z.string()),
  lines: z.array(SettingLineDelta),
  overdue: SettingSide,
  in_horizon: SettingSide,
  beyond: SettingSide,
  edge_before: z.iso.date().nullable(),
  edge_after: z.iso.date().nullable(),
  surfaces: z.array(z.string()),
  formulas: z.array(z.string()),
  basis: z.string(),
  recommendation: z.string().nullable(),
  regular: SettingRegular.nullable(),
  buffer: SettingSide.nullable(),
});
export type SettingPreview = z.infer<typeof SettingPreview>;

/** `POST /api/s-11/apply`: записано — новая запись истории «з сьогодні». */
export const SettingApplied = z.strictObject({
  code: z.string(),
  value_before: z.union([z.boolean(), z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")), z.string().min(1)]).nullable(),
  value_after: z.union([z.boolean(), z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")), z.string().min(1)]),
  applies_from: z.iso.date(),
  actor: z.string(),
  recommendation: z.string().nullable(),
});
export type SettingApplied = z.infer<typeof SettingApplied>;

/** Отказ значения: код случая (`settings.service.REFUSED_*`) — слово к нему словарь экрана; `ПР` — `changed_meanwhile` с текущим значением и автором. */
export const SettingRefusedAnswer = z.strictObject({
  kind: z.literal("setting_refused"),
  reason: z.string().min(1),
  code: z.string(),
  current: z.union([z.boolean(), z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")), z.string().min(1)]).nullable(),
  changed_by: z.string().nullable(),
  changed_on: z.iso.date().nullable(),
});
export type SettingRefusedAnswer = z.infer<typeof SettingRefusedAnswer>;

/** «N з M без внесеного залишку»: `count` кошельков расчёта на реконструкции. */
export const WalletsCount = z.strictObject({
  count: z.number().int(),
  total: z.number().int(),
});
export type WalletsCount = z.infer<typeof WalletsCount>;

/** Строка кошелька `GET /api/s-04/wallets`: остаток на конец `on` — один дом (`wallet_balances`); якорь — ввод (`entered`) или реконструкция (`rebuilt`), к нему движение `moved_from … on`; `FRM-75` последнего ввода и его давность. */
export const WalletRow = z.strictObject({
  key: z.string(),
  title: z.string().nullable(),
  kind: z.enum(["cashbox", "account"]),
  in_calculation: z.boolean(),
  value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  base: z.string(),
  source: z.enum(["entered", "rebuilt"]),
  entered: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  entered_day: z.iso.date().nullable(),
  entered_by: z.string().nullable(),
  moved: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  moved_from: z.iso.date().nullable(),
  discrepancy: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  discrepancy_shown: z.boolean(),
  age_days: z.number().int().nullable(),
  minus: z.boolean(),
  closed_value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type WalletRow = z.infer<typeof WalletRow>;

/** `GET /api/s-04/wallets?on=`: строки кошельков; Σ строк расчёта = `total` (`FRM-10` того же дня). */
export const WalletsScreen = z.strictObject({
  mark: z.enum(["РЕД", "ТЧ", "НВ"]),
  on: z.iso.date(),
  today: z.iso.date(),
  launched: z.boolean(),
  total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  total_basis: z.string(),
  without: WalletsCount.nullable(),
  sync_settled: z.boolean(),
  closed_day: z.iso.date(),
  rows: z.array(WalletRow),
});
export type WalletsScreen = z.infer<typeof WalletsScreen>;

/** `POST /api/s-04/wallets/{key}/preview`: «було → стало» ввода — ничего не пишет. «Було» и «стало» — на один конец дня `day`; «сьогодні» и «Вільно» — на `on` до и после; Δ «Вільно» 0,00 — с причиной `free_reason`. */
export const WalletPreviewAnswer = z.strictObject({
  key: z.string(),
  day: z.iso.date(),
  on: z.iso.date(),
  was: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  becomes: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  delta: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  base_was: z.string(),
  base_becomes: z.string(),
  today_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  today_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  base_today: z.string(),
  moved_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  moved_from: z.iso.date().nullable(),
  moved_to: z.iso.date().nullable(),
  in_calculation: z.boolean(),
  shadowed_day: z.iso.date().nullable(),
  shadowed_amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  balance_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  balance_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_delta: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  free_reason: z.enum(["shadowed", "not_in_calculation"]).nullable(),
  base_free: z.string(),
  without_before: WalletsCount.nullable(),
  without_after: WalletsCount.nullable(),
  discrepancy: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  discrepancy_expected: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  discrepancy_shown: z.boolean(),
  threshold: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  jump: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  jump_over: z.boolean(),
  now_sum: z.boolean(),
});
export type WalletPreviewAnswer = z.infer<typeof WalletPreviewAnswer>;

/** `POST /api/s-04/wallets/{key}/balance`: записано — остаток на конец `day`. */
export const WalletBalanceSaved = z.strictObject({
  key: z.string(),
  day: z.iso.date(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  actor: z.string(),
  version: z.number().int(),
  today_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  balance_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type WalletBalanceSaved = z.infer<typeof WalletBalanceSaved>;

/** Одно внесение остатка: на конец `day` — `amount`; записано `recorded_on`, кем и по какому основанию. */
export const WalletHistoryEntry = z.strictObject({
  day: z.iso.date(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  actor: z.string(),
  recorded_on: z.iso.date().nullable(),
  basis: z.string().nullable(),
});
export type WalletHistoryEntry = z.infer<typeof WalletHistoryEntry>;

/** `GET /api/s-04/wallets/{key}/history`: кто, когда и что внёс — новое сверху. */
export const WalletHistory = z.strictObject({
  key: z.string(),
  entries: z.array(WalletHistoryEntry),
});
export type WalletHistory = z.infer<typeof WalletHistory>;

/** Документ движения кошелька: вид (`payment-42`, `cashorder-25`…), день, вклад в остаток со знаком (расход — минус), номер и название контрагента, если они есть в источнике. */
export const WalletDocument = z.strictObject({
  kind: z.string(),
  external_id: z.string(),
  day: z.iso.date().nullable(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  number: z.string().nullable(),
  partner_title: z.string().nullable(),
});
export type WalletDocument = z.infer<typeof WalletDocument>;

/** `GET /api/s-04/wallets/{key}/documents?on=&offset=&limit=`: раскрытие `РД` строки — документы движения после якоря до `on`, тем же планом, что `value` строки (`wallet_documents`). Якорь — `anchor` (внесённый остаток; у реконструкции `null`, она от нуля с `CFG-01`); `anchor` + `docs_sum` (Σ **всех** документов, не страницы) = `value` до копейки. `items` — страница (новые сверху), `count` — всего, `more` — есть ли дальше. */
export const WalletDocuments = z.strictObject({
  key: z.string(),
  on: z.iso.date(),
  value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  anchor: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  anchor_day: z.iso.date().nullable(),
  moved_from: z.iso.date().nullable(),
  docs_sum: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  count: z.number().int(),
  offset: z.number().int(),
  more: z.boolean(),
  items: z.array(WalletDocument),
});
export type WalletDocuments = z.infer<typeof WalletDocuments>;

/** Отказ ручки `S-04`: код случая и причина кодом (`balance_day_not_over`, …). */
export const WalletRefused = z.strictObject({
  kind: z.enum(["wallet_not_found", "balance_refused", "stale", "sync_not_settled"]),
  reason: z.string().min(1).nullable(),
  pair: z.string().nullable().optional(),
});
export type WalletRefused = z.infer<typeof WalletRefused>;

/** «Не збігається з рухом»: `FRM-75` ввода, действующего на `on`, = `discrepancy` строки `S-04` того же дня; показ от `CFG-31`. */
export const MismatchDiscrepancy = z.strictObject({
  key: z.string(),
  title: z.string().nullable(),
  kind: z.enum(["cashbox", "account"]),
  day: z.iso.date(),
  entered: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  expected: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  threshold: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  base: z.string(),
});
export type MismatchDiscrepancy = z.infer<typeof MismatchDiscrepancy>;

/** «Мінус на кінець дня»: закрытые дни после якоря (`since`), первый и последний, число дней, наименьший остаток. */
export const MismatchMinus = z.strictObject({
  key: z.string(),
  title: z.string().nullable(),
  kind: z.enum(["cashbox", "account"]),
  first: z.iso.date(),
  last: z.iso.date(),
  days: z.number().int(),
  lowest: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  since: z.iso.date(),
  base: z.string(),
});
export type MismatchMinus = z.infer<typeof MismatchMinus>;

/** Половина пары: остаток на конец `on`, первый день движения с `CFG-01`. */
export const MismatchHalf = z.strictObject({
  key: z.string(),
  title: z.string().nullable(),
  value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  entered: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  entered_day: z.iso.date().nullable(),
  moved_since: z.iso.date().nullable(),
  moved: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  documents: z.number().int(),
  base: z.string(),
});
export type MismatchHalf = z.infer<typeof MismatchHalf>;

/** «Один рахунок — два гаманці»: предложенные цель и источник; `doubled` — внесено ненулевое по обеим (те же деньги вдвое). */
export const MismatchPair = z.strictObject({
  target: z.string(),
  source: z.string(),
  mergeable: z.boolean(),
  doubled: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  halves: z.array(MismatchHalf),
});
export type MismatchPair = z.infer<typeof MismatchPair>;

/** «Злиті гаманці»: слияние, которое не откатывали. */
export const MergedWallet = z.strictObject({
  merge_id: z.number().int(),
  target: z.string(),
  source: z.string(),
  target_title: z.string().nullable(),
  source_title: z.string().nullable(),
  merged_at: z.iso.datetime({ offset: true }),
  actor: z.string(),
});
export type MergedWallet = z.infer<typeof MergedWallet>;

/** `GET /api/s-18/wallets?on=`: блок «Не сходиться» — строки считаются на запрос; `total` — `FRM-10` того же дня (блок денег не двигает); `synced_at` — время сверки, «за даними DnTrade на …». */
export const WalletMismatch = z.strictObject({
  mark: z.enum(["РЕД", "ТЧ", "НВ"]),
  on: z.iso.date(),
  today: z.iso.date(),
  count: z.number().int(),
  wallets: z.number().int(),
  matched: z.number().int(),
  total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  synced_at: z.iso.datetime({ offset: true }).nullable(),
  sync_settled: z.boolean(),
  discrepancies: z.array(MismatchDiscrepancy),
  minuses: z.array(MismatchMinus),
  pairs: z.array(MismatchPair),
  merged: z.array(MergedWallet),
});
export type WalletMismatch = z.infer<typeof WalletMismatch>;

/** `POST /api/s-18/wallets/merge/preview`: план и «Вільно» до и после — ничего не пишет; `fingerprint` — запись сверяет его (`ПР`). */
export const WalletMergePreview = z.strictObject({
  target: z.string(),
  source: z.string(),
  on: z.iso.date(),
  moves: z.number().int(),
  keeps: z.number().int(),
  source_value: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  balance_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  balance_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  base: z.string(),
  fingerprint: z.string(),
});
export type WalletMergePreview = z.infer<typeof WalletMergePreview>;

/** `POST /api/s-18/wallets/merge` и откат: факт после записи, посчитанный заново. */
export const WalletMerged = z.strictObject({
  merge_id: z.number().int(),
  target: z.string(),
  source: z.string(),
  balance_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type WalletMerged = z.infer<typeof WalletMerged>;

/** Отказ ручки `S-18`: код случая и причина кодом; у движения источника — сумма и день. */
export const MismatchRefused = z.strictObject({
  kind: z.enum(["merge_refused", "stale", "sync_not_settled", "no_facts"]),
  reason: z.string().min(1).nullable(),
  pair: z.string().nullable().optional(),
  moved: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable().optional(),
  since: z.iso.date().nullable().optional(),
});
export type MismatchRefused = z.infer<typeof MismatchRefused>;

/** Редакция правила статьи (`ENT-207`): сумма пусто — «за історією». */
export const ExpenseEdition = z.strictObject({
  rule: z.string(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  by_history: z.boolean(),
  valid_from: z.iso.date(),
  valid_to: z.iso.date().nullable(),
  actor: z.string().nullable(),
  note: z.string().nullable(),
});
export type ExpenseEdition = z.infer<typeof ExpenseEdition>;

/** Дата правила после разнесения оплат: сумма, оплачено, остаток. */
export const ExpenseDate = z.strictObject({
  key: z.string(),
  day: z.iso.date(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  by_history: z.boolean(),
  paid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  overdue: z.boolean(),
  closed: z.string().nullable(),
  silent: z.boolean(),
});
export type ExpenseDate = z.infer<typeof ExpenseDate>;

/** Подсказка правила из истории (правило 8): день крупнейшего платежа месяца. */
export const ExpenseHint = z.strictObject({
  day_from: z.number().int(),
  day_to: z.number().int(),
  months_hit: z.number().int(),
  months_total: z.number().int(),
  rule: z.string(),
});
export type ExpenseHint = z.infer<typeof ExpenseHint>;

/** Статья расхода `S-06`: регулярна и почему, среднее месяца, способ входа. */
export const ExpenseArticleRow = z.strictObject({
  article: z.string(),
  title: z.string().nullable(),
  regular: z.boolean(),
  why: z.enum(["threshold", "added", "removed", "below"]),
  reason: z.string().nullable(),
  present_in: z.number().int(),
  base_months: z.number().int().nullable(),
  month_average: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  mode: z.enum(["obligation", "dates", "sum"]).nullable(),
  rule: ExpenseEdition.nullable(),
  editions: z.array(ExpenseEdition),
  dates: z.array(ExpenseDate),
  excess: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  amount_unknown: z.string().nullable(),
  horizon_share: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  hint: ExpenseHint.nullable(),
});
export type ExpenseArticleRow = z.infer<typeof ExpenseArticleRow>;

/** `GET /api/s-06/articles?on=`: Σ средних регулярных = `FRM-38` до копейки (разнесение копеек наибольшим остатком, поля «округлення» нет — правило 4); `sum_part` и `dated_part` — части строки обязательств `S-01` того же дня. */
export const ExpenseArticles = z.strictObject({
  mark: z.string(),
  on: z.iso.date(),
  today: z.iso.date(),
  horizon: z.number().int().nullable(),
  base_from: z.iso.date().nullable(),
  base_to: z.iso.date().nullable(),
  base_months: z.number().int().nullable(),
  threshold: z.number().int().nullable(),
  regular_month: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  regular_basis: z.string(),
  regular_missing: z.array(z.string()),
  sum_part: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  dated_part: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  parts_total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  parts_unknown: z.array(z.string()),
  sync_settled: z.boolean(),
  rows: z.array(ExpenseArticleRow),
});
export type ExpenseArticles = z.infer<typeof ExpenseArticles>;

/** Кусок документа оплаты, легший на дату правила. */
export const ExpensePiece = z.strictObject({
  key: z.string(),
  date: z.iso.date(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
});
export type ExpensePiece = z.infer<typeof ExpensePiece>;

/** Документ оплаты статьи (`dt=45`/`dt=23`) и куда он лёг: куски и излишек. */
export const ExpensePaidDocument = z.strictObject({
  kind: z.string(),
  external_id: z.string(),
  day: z.iso.date().nullable(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  pieces: z.array(ExpensePiece),
  excess: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
});
export type ExpensePaidDocument = z.infer<typeof ExpensePaidDocument>;

/** `GET /api/s-06/articles/{article}/payments?on=`: документы оплат статьи в окне разнесения и куски по датам (`РД`); `paid` = `pieces` + `excess` до копейки. */
export const ExpensePayments = z.strictObject({
  article: z.string(),
  on: z.iso.date(),
  window_from: z.iso.date().nullable(),
  documents: z.array(ExpensePaidDocument),
  paid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  pieces: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  excess: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
});
export type ExpensePayments = z.infer<typeof ExpensePayments>;

export const ExpenseRuleDate = z.strictObject({
  day: z.iso.date(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  paid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  overdue: z.boolean(),
});
export type ExpenseRuleDate = z.infer<typeof ExpenseRuleDate>;

/** Дата правила, перенесённая с выходного (решение 025): день оплаты и день по правилу. */
export const ExpenseRuleShift = z.strictObject({
  day: z.iso.date(),
  rule_day: z.iso.date(),
});
export type ExpenseRuleShift = z.infer<typeof ExpenseRuleShift>;

/** `POST /api/s-06/rule/preview`: «Вільно» було → стане от правила — ничего не пишет. */
export const ExpenseRulePreview = z.strictObject({
  article: z.string(),
  rule: z.string(),
  on: z.iso.date(),
  horizon: z.number().int().nullable(),
  free_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  delta: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  dated_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  dated_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  mode_before: z.enum(["obligation", "dates", "sum"]).nullable(),
  mode_after: z.enum(["obligation", "dates", "sum"]).nullable(),
  dates: z.array(z.iso.date()),
  shifted: z.array(ExpenseRuleShift),
  in_horizon: z.array(ExpenseRuleDate),
  by_history: z.boolean(),
  amount_basis: z.string(),
  basis_before: z.string(),
  basis_after: z.string(),
  seen: z.string().nullable(),
});
export type ExpenseRulePreview = z.infer<typeof ExpenseRulePreview>;

/** `POST /api/s-06/rule`, `…/rule/stop`, `…/dates/close`: записано — факт после записи. */
export const ExpenseRuleSaved = z.strictObject({
  article: z.string(),
  actor: z.string(),
  version: z.number().int(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type ExpenseRuleSaved = z.infer<typeof ExpenseRuleSaved>;

/** `POST /api/s-06/regular/preview`: поправка `CFG-21` — «Вільно» було → стане. */
export const ExpenseRegularPreview = z.strictObject({
  article: z.string(),
  action: z.string(),
  on: z.iso.date(),
  free_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  delta: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  complete: z.boolean(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
  sum_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  regular_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  regular_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  regular_basis_before: z.string().nullable(),
  regular_basis_after: z.string().nullable(),
  keeps_dates: z.boolean(),
  basis: z.string(),
  seen: z.string(),
});
export type ExpenseRegularPreview = z.infer<typeof ExpenseRegularPreview>;

export const ExpenseRegularSaved = z.strictObject({
  article: z.string(),
  action: z.string(),
  actor: z.string(),
  applies_from: z.iso.date(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type ExpenseRegularSaved = z.infer<typeof ExpenseRegularSaved>;

/** Отказ ручки `S-06`: код случая и причина текстом ядра. */
export const ExpenseRefused = z.strictObject({
  kind: z.enum(["rule_refused", "regular_refused", "stale", "no_rule", "sync_not_settled"]),
  reason: z.string().nullable(),
});
export type ExpenseRefused = z.infer<typeof ExpenseRefused>;

/** Дата графика кредита после разнесения: тело и проценты — два выхода. */
export const LoanDateRow = z.strictObject({
  due: z.iso.date(),
  pay_day: z.iso.date(),
  body: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  interest: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  rest: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  manual: z.boolean(),
  body_paid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  interest_paid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  body_left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  interest_left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  interest_diff: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  open: z.boolean(),
  overdue: z.boolean(),
});
export type LoanDateRow = z.infer<typeof LoanDateRow>;

export const LoanDocRow = z.strictObject({
  kind: z.string(),
  external_id: z.string(),
  day: z.iso.date().nullable(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type LoanDocRow = z.infer<typeof LoanDocRow>;

export const LoanEarlyRow = z.strictObject({
  day: z.iso.date(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type LoanEarlyRow = z.infer<typeof LoanEarlyRow>;

/** Кредитная линия: договор DnTrade, условия, остаток тела с базой, график. */
export const LoanLineRow = z.strictObject({
  key: z.string(),
  agreement: z.string().nullable(),
  agreement_title: z.string().nullable(),
  partner: z.string().nullable(),
  partner_title: z.string().nullable(),
  state: z.enum(["terms_ok", "terms_missing"]),
  missing: z.array(z.string()),
  kind: z.string().nullable(),
  rate: z.string().nullable(),
  first: z.iso.date().nullable(),
  final: z.iso.date().nullable(),
  category: z.string().nullable(),
  article: z.string().nullable(),
  article_title: z.string().nullable(),
  article_origin: z.string().nullable(),
  creditor: z.string().nullable(),
  early_term: z.string().nullable(),
  critical: z.boolean(),
  remainder: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  remainder_basis: z.string(),
  received: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  received_on: z.iso.date().nullable(),
  repaid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  repaid_pct: z.number().int().nullable(),
  closed_on: z.iso.date().nullable(),
  active: z.boolean(),
  interest_in_article: z.boolean(),
  next: LoanDateRow.nullable(),
  due: z.array(LoanDateRow),
  in_horizon: z.array(LoanDateRow),
  schedule: z.array(LoanDateRow),
  sum_body: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_interest: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  early: z.array(LoanEarlyRow),
  silent: z.array(z.iso.date()),
  early_check: z.boolean(),
  tranche_check: z.boolean(),
  documents: z.array(LoanDocRow),
  seen: z.string().nullable(),
});
export type LoanLineRow = z.infer<typeof LoanLineRow>;

export const LoanGap = z.strictObject({
  kind: z.string().nullable(),
  numbers: z.record(z.string(), z.union([z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$"))])),
});
export type LoanGap = z.infer<typeof LoanGap>;

/** Пара `CFG-109` «категорія зобов'язання → стаття витрат». */
export const LoanArticlePair = z.strictObject({
  category: z.string(),
  article: z.string(),
});
export type LoanArticlePair = z.infer<typeof LoanArticlePair>;

/** Кандидат в кредиторы: контрагент с договором и поступлением денег в DnTrade. */
export const CreditorRow = z.strictObject({
  key: z.string(),
  title: z.string().nullable(),
  category: z.string().nullable(),
  category_title: z.string().nullable(),
  agreements: z.number().int(),
  first_agreement: z.string().nullable(),
  more: z.number().int(),
  received: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  bank: z.boolean(),
  priority: z.boolean(),
  side: z.string().nullable(),
  side_warning: z.boolean(),
});
export type CreditorRow = z.infer<typeof CreditorRow>;

/** Статья расходов для выбора: число платежей, рекомендуемая, кем уже занята. */
export const ArticleOption = z.strictObject({
  id: z.string(),
  title: z.string().nullable(),
  count: z.number().int(),
  recommended: z.boolean(),
  taken_by: z.string().nullable(),
});
export type ArticleOption = z.infer<typeof ArticleOption>;

/** `GET /api/s-13/loans`: линии, часть (5) «Вільно» и месяц по дате договора. */
export const LoansScreen = z.strictObject({
  mark: z.string(),
  edit_terms: z.boolean(),
  edit_owner: z.boolean(),
  on: z.iso.date(),
  today: z.iso.date(),
  horizon: z.number().int().nullable(),
  edge: z.iso.date().nullable(),
  free_part: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  part_body: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  part_interest: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  part_basis: z.string().nullable(),
  part_since: z.iso.date().nullable(),
  without_terms: z.number().int(),
  gaps: z.array(LoanGap),
  free_side: z.enum(["завищено", "занижено", "невідомо", "в обидва боки"]).nullable(),
  free_reasons: z.array(FreeGap),
  month: z.string(),
  month_body: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  month_interest: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  month_total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  month_basis: z.string(),
  month_paid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  remainder_total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  remainder_unknown: z.number().int(),
  article_map: z.array(LoanArticlePair),
  article_seen: z.string(),
  creditors: z.array(CreditorRow),
  creditors_seen: z.string(),
  creditors_hidden: z.number().int(),
  creditors_selected: z.number().int(),
  creditors_agreements: z.number().int(),
  loans_found: z.number().int(),
  article_options: z.array(ArticleOption),
  sync_settled: z.boolean(),
  lines: z.array(LoanLineRow),
});
export type LoansScreen = z.infer<typeof LoansScreen>;

/** `POST /api/s-13/terms/preview`: «Вільно» було → стане от условий — ничего не пишет. */
export const LoanTermsPreview = z.strictObject({
  key: z.string(),
  on: z.iso.date(),
  horizon: z.number().int().nullable(),
  free_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  delta: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  loans_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  loans_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  mode_before: z.string().nullable(),
  mode_after: z.string().nullable(),
  state: z.enum(["terms_ok", "terms_missing"]).nullable(),
  missing: z.array(z.string()),
  schedule: z.array(LoanDateRow),
  sum_body: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_interest: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  basis_before: z.string(),
  basis_after: z.string(),
  base_from: z.iso.date().nullable(),
  base_to: z.iso.date().nullable(),
  seen: z.string().nullable(),
});
export type LoanTermsPreview = z.infer<typeof LoanTermsPreview>;

export const LoanTermsSaved = z.strictObject({
  key: z.string(),
  actor: z.string(),
  version: z.number().int(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type LoanTermsSaved = z.infer<typeof LoanTermsSaved>;

/** `POST /api/s-13/creditors/preview`: «знайдемо кредитів» и «Вільно» було → стане. */
export const CreditorsPreview = z.strictObject({
  on: z.iso.date(),
  horizon: z.number().int().nullable(),
  banks: z.number().int(),
  agreements: z.number().int(),
  candidates: z.number().int(),
  lines_before: z.number().int(),
  lines_after: z.number().int(),
  free_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  delta: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  gaps: z.array(LoanGap),
  basis: z.string(),
  seen: z.string(),
});
export type CreditorsPreview = z.infer<typeof CreditorsPreview>;

export const CreditorsSaved = z.strictObject({
  banks: z.number().int(),
  actor: z.string(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type CreditorsSaved = z.infer<typeof CreditorsSaved>;

/** `POST /api/s-13/article/preview`: пара `CFG-109` — «Вільно» було → стане. */
export const LoanArticlePreview = z.strictObject({
  category: z.string(),
  article: z.string().nullable(),
  articles: z.array(z.string()),
  on: z.iso.date(),
  free_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  delta: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  complete: z.boolean(),
  basis: z.string(),
  seen: z.string(),
});
export type LoanArticlePreview = z.infer<typeof LoanArticlePreview>;

export const LoanArticleSaved = z.strictObject({
  category: z.string(),
  article: z.string().nullable(),
  articles: z.array(z.string()),
  actor: z.string(),
  applies_from: z.iso.date(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type LoanArticleSaved = z.infer<typeof LoanArticleSaved>;

/** Отказ ручки `S-13`: код случая и причина текстом ядра. */
export const LoanRefused = z.strictObject({
  kind: z.enum(["terms_refused", "article_refused", "creditors_refused", "field_owner_only", "stale", "sync_not_settled"]),
  reason: z.string().nullable(),
});
export type LoanRefused = z.infer<typeof LoanRefused>;

/** Дата выплаты человека после разнесения выплат (`TRAP-27`). */
export const PayDateRow = z.strictObject({
  kind: z.enum(["advance", "salary"]),
  month: z.string(),
  due: z.iso.date(),
  pay_day: z.iso.date(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  paid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  state: z.enum(["expected", "window", "overdue", "paid", "closed"]),
  until: z.iso.date(),
  partial: z.boolean(),
  basis: z.string(),
  documents: z.array(LoanDocRow),
  shortfall: z.boolean(),
  closed_on: z.iso.date().nullable(),
  closed_reason: z.string().nullable(),
});
export type PayDateRow = z.infer<typeof PayDateRow>;

export const RateRow = z.strictObject({
  valid_from: z.iso.date(),
  valid_to: z.iso.date().nullable(),
  rate: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type RateRow = z.infer<typeof RateRow>;

/** Человек `S-14`: карточка `ENT-06` или контрагент «співробітник» без неё. */
export const StaffRow = z.strictObject({
  key: z.string(),
  card: z.boolean(),
  title: z.string().nullable(),
  name: z.string().nullable(),
  surname: z.string().nullable(),
  position: z.string().nullable(),
  role: z.string().nullable(),
  rate: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  rates: z.array(RateRow),
  commission: z.boolean(),
  commission_rate: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  kpi: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  client_categories: z.array(z.string()),
  salary_day: z.number().int().nullable(),
  advance_day: z.number().int().nullable(),
  advance_sum: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  starts_on: z.iso.date().nullable(),
  ends_on: z.iso.date().nullable(),
  dismissed: z.boolean(),
  counted: z.boolean(),
  channels: z.array(z.string()),
  next: z.array(PayDateRow),
  schedule: z.array(PayDateRow),
  silent: z.array(z.iso.date()),
  seen: z.string().nullable(),
});
export type StaffRow = z.infer<typeof StaffRow>;

/** Одна ступень шкалы комиссии: часть базы, ставка, сумма ступени. Σ сумм ступеней = комиссии человека до копейки (копейки раздаёт `fortify.money.share`). */
export const StepRow = z.strictObject({
  low: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  top: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  part: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  rate: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
});
export type StepRow = z.infer<typeof StepRow>;

/** Месяц человека в `FRM-72`: ставка по рабочим дням, комиссия, KPI, бонусы. */
export const PersonMonthRow = z.strictObject({
  key: z.string(),
  worked: z.number().int(),
  days: z.number().int(),
  rate: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  commission: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  kpi: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  bonus: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  inflow: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  closed: z.boolean(),
  partial: z.boolean(),
  unmapped: z.boolean(),
  steps: z.array(StepRow),
  bar_bp: z.number().int(),
  of_total_pct: z.number().int().nullable(),
});
export type PersonMonthRow = z.infer<typeof PersonMonthRow>;

/** Категория контрагентов DnTrade — «Категорії клієнтів» менеджера. */
export const CategoryRow = z.strictObject({
  id: z.string(),
  title: z.string(),
});
export type CategoryRow = z.infer<typeof CategoryRow>;

/** `FRM-72` месяца — две базы рядом, не складываются (`TRAP-24`). */
export const PayrollMonthRow = z.strictObject({
  month: z.string(),
  month_min: z.string().nullable(),
  month_max: z.string(),
  rates: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  commissions: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  kpi: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  bonuses: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  unassigned: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  accrued: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  accrued_basis: z.string(),
  paid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  paid_basis: z.string(),
  partial: z.boolean(),
  closed: z.boolean(),
  days: z.number().int().nullable(),
  fixed: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  variable: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  fixed_pct: z.number().int().nullable(),
  variable_pct: z.number().int().nullable(),
  left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  pay_by: z.iso.date().nullable(),
  people: z.array(PersonMonthRow),
});
export type PayrollMonthRow = z.infer<typeof PayrollMonthRow>;

/** `GET /api/s-14/people`: люди, даты выплат, часть «Зарплата (ФОП)» и ФОТ месяца. */
export const PeopleScreen = z.strictObject({
  mark: z.string(),
  on: z.iso.date(),
  today: z.iso.date(),
  horizon: z.number().int().nullable(),
  edge: z.iso.date().nullable(),
  free_part: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  part_basis: z.string().nullable(),
  gaps: z.array(LoanGap),
  free_side: z.enum(["завищено", "занижено", "невідомо", "в обидва боки"]).nullable(),
  free_reasons: z.array(FreeGap),
  full: z.boolean(),
  with_rate: z.number().int(),
  members: z.number().int(),
  article: z.string().nullable(),
  founders_article: z.string().nullable(),
  articles: z.array(z.string()),
  founders_articles: z.array(z.string()),
  article_options: z.array(ArticleOption),
  article_seen: z.string(),
  without_rate: z.number().int(),
  channels_without_person: z.number().int().nullable(),
  categories: z.array(CategoryRow),
  salary_window: z.array(z.number().int()),
  salary_day_default: z.number().int().nullable(),
  advance_window: z.array(z.number().int()),
  month: PayrollMonthRow,
  sync_settled: z.boolean(),
  people: z.array(StaffRow),
});
export type PeopleScreen = z.infer<typeof PeopleScreen>;

/** `POST /api/s-14/person/preview`: «Вільно» було → стане от события — ничего не пишет. */
export const PersonPreview = z.strictObject({
  key: z.string(),
  action: z.enum(["hire", "rate", "days", "dismiss", "close", "reopen", "categories"]),
  on: z.iso.date(),
  horizon: z.number().int().nullable(),
  free_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  delta: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  payroll_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  payroll_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  sum_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  mode_before: z.string().nullable(),
  mode_after: z.string().nullable(),
  with_rate: z.number().int(),
  members: z.number().int(),
  schedule: z.array(PayDateRow),
  basis_before: z.string(),
  basis_after: z.string(),
  seen: z.string().nullable(),
  gaps: z.array(LoanGap),
  month: z.string().nullable(),
  person_rate_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  person_rate_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  commission_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  commission_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  rates_before: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  rates_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  worked: z.number().int().nullable(),
  days: z.number().int().nullable(),
});
export type PersonPreview = z.infer<typeof PersonPreview>;

export const PersonSaved = z.strictObject({
  key: z.string(),
  action: z.enum(["hire", "rate", "days", "dismiss", "close", "reopen", "categories"]),
  actor: z.string(),
  version: z.number().int(),
  free_after: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type PersonSaved = z.infer<typeof PersonSaved>;

/** Отказ ручки `S-14`: код случая и причина текстом ядра. */
export const PeopleRefused = z.strictObject({
  kind: z.enum(["person_refused", "article_refused", "month_refused", "stale", "sync_not_settled"]),
  reason: z.string().nullable(),
});
export type PeopleRefused = z.infer<typeof PeopleRefused>;

/** Кто: id (контрагент, человек, статья) и название — `None`, если справочник молчит. */
export const DutyWho = z.strictObject({
  id: z.string().nullable(),
  title: z.string().nullable(),
});
export type DutyWho = z.infer<typeof DutyWho>;

/** Пробел вида: код случая, числа, сторона ошибки для «Вільно». */
export const DutyGap = z.strictObject({
  kind: z.string().nullable(),
  numbers: z.record(z.string(), z.union([z.number().int(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")), z.string()])),
  direction: z.enum(["завищено", "занижено", "невідомо"]),
  in_sum: z.boolean(),
});
export type DutyGap = z.infer<typeof DutyGap>;

/** Вид обязательств: сумма (`None` — числа нет), база словами, экран вида. */
export const DutyKind = z.strictObject({
  kind: z.string().min(1),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  basis: z.string(),
  surface: z.string(),
  in_development: z.boolean(),
  gaps: z.array(DutyGap),
});
export type DutyKind = z.infer<typeof DutyKind>;

/** Строка: кто, за что, термін → день оплаты, сплачено, залишок (в сумме). */
export const DutyRow = z.strictObject({
  kind: z.string().min(1),
  key: z.string(),
  who: DutyWho,
  what: z.string().nullable(),
  what_title: z.string().nullable(),
  due: z.iso.date().nullable(),
  pay_day: z.iso.date().nullable(),
  offset: z.number().int().nullable(),
  part: z.enum(["overdue", "in_horizon", "estimate", "later"]),
  in_horizon: z.boolean(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  paid: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  body_left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  interest_left: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  marks: z.array(z.string().min(1)),
  mode: z.string().min(1).nullable(),
  why: z.string().nullable(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
  movable: z.boolean(),
});
export type DutyRow = z.infer<typeof DutyRow>;

/** Группа: прострочено · день оплаты · без дати; сумма — сервера (правило 4). */
export const DutyDay = z.strictObject({
  group: z.enum(["overdue", "day", "no_date"]),
  word: z.string().min(1).nullable(),
  day: z.iso.date().nullable(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  rows: z.number().int(),
  in_horizon: z.boolean(),
  by_kind: z.record(z.string(), z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$"))),
  unknown: z.number().int(),
});
export type DutyDay = z.infer<typeof DutyDay>;

/** Счётчик вне суммы: штуки, сумма (`None` — неизвестна), сторона ошибки. */
export const DutyCounter = z.strictObject({
  kind: z.string().min(1),
  count: z.number().int(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  direction: z.enum(["завищено", "занижено", "невідомо"]).nullable(),
  in_sum: z.boolean(),
});
export type DutyCounter = z.infer<typeof DutyCounter>;

/** Платёж графика старого долга (`ENT-36`) до края — вне суммы (020, 044, 063). `day` — `None`: «прострочено за графіком». */
export const DutyOldDebt = z.strictObject({
  partner: z.string(),
  title: z.string().nullable(),
  day: z.iso.date().nullable(),
  amount: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
});
export type DutyOldDebt = z.infer<typeof DutyOldDebt>;

/** `GET /api/s-30/obligations?on=&edge=` (F-023): обязательства всех видов по дням. `total` — строка «Зобов'язання в горизонті H» «Вільно» того же расчёта (`free_line` — она же из разложения «Вільно»); Σ `days[in_horizon]` = `total`. */
export const ObligationsPage = z.strictObject({
  mark: z.string(),
  on: z.iso.date(),
  edge: z.iso.date().nullable(),
  edge_asked: z.iso.date().nullable(),
  horizon: z.number().int().nullable(),
  horizon_edge: z.iso.date().nullable(),
  launch: z.iso.date().nullable(),
  basis: z.string(),
  total: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_line: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")).nullable(),
  free_side: z.enum(["завищено", "занижено", "невідомо", "в обидва боки"]).nullable(),
  overdue: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  in_horizon: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  estimate: z.string().regex(new RegExp("^-?\\d+(\\.\\d+)?$")),
  kinds: z.array(DutyKind),
  rows: z.array(DutyRow),
  days: z.array(DutyDay),
  counters: z.array(DutyCounter),
  old_debt: z.array(DutyOldDebt),
});
export type ObligationsPage = z.infer<typeof ObligationsPage>;

export const ObligationsRefused = z.strictObject({
  kind: z.string().min(1),
});
export type ObligationsRefused = z.infer<typeof ObligationsRefused>;
